import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from "react";
import {
  Camera,
  CheckCircle2,
  Eye,
  EyeOff,
  Filter,
  ImagePlus,
  Minus,
  PackageX,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  Upload,
  Warehouse,
} from "lucide-react";
import {
  Button,
  EmptyState,
  ErrorState,
  MediaImage,
  Modal,
  Notice,
  Skeleton,
  StatusBadge,
} from "../components/ui";
import { useSession } from "../context/SessionContext";
import { apiRequest, jsonBody, queryString } from "../lib/api";
import { formatDateTime, formatRupiah, statusLabel } from "../lib/format";
import { updateSelectedBranchIds } from "../lib/product-branch-selection";
import {
  blobToBase64,
  optimizeProductImage,
  type OptimizedImage,
} from "../lib/images";
import {
  normalizeCollection,
  type ApiRecord,
  type SessionUser,
} from "../lib/types";

const value = (record: ApiRecord, ...keys: string[]) =>
  keys
    .map((key) => record[key])
    .find((item) => item !== undefined && item !== null);
const text = (item: unknown, fallback = "") =>
  typeof item === "string" || typeof item === "number"
    ? String(item)
    : fallback;
const number = (item: unknown, fallback = 0) =>
  Number.isFinite(Number(item)) ? Number(item) : fallback;
const nullableNumber = (record: ApiRecord, ...keys: string[]) => {
  for (const key of keys) {
    if (key in record) {
      const item = record[key];
      return item === null || item === undefined || item === ""
        ? null
        : number(item);
    }
  }
  return null;
};
const bool = (item: unknown, fallback = false) =>
  item === true || item === 1 || item === "1"
    ? true
    : item === false || item === 0 || item === "0"
      ? false
      : fallback;

interface ProductBranch {
  branchId: string;
  branchName: string;
  price: number;
  promoPrice: number | null;
  unit: string;
  stock: number;
  minimumStock: number;
  unlimited: boolean;
  available: boolean;
  featured: boolean;
  mediaId?: string;
  branchMediaId?: string;
}

interface ProductRow {
  id: string;
  name: string;
  description: string;
  categoryId: string;
  categoryIds: string[];
  categoryName: string;
  categoryNames: string[];
  branch: ProductBranch;
}

interface MediaOption {
  id: string;
  filename: string;
}

interface CategoryOption {
  id: string;
  name: string;
  slug: string;
  description: string;
  mediaId?: string;
  selectedMediaId?: string;
  navigationOrder?: number;
}

interface BranchOption {
  id: string;
  name: string;
}

interface NewProductForm {
  categoryIds: string[];
  branchIds: string[];
  name: string;
  description: string;
  price: string;
  promoPrice: string;
  unit: string;
  stock: string;
  unlimited: boolean;
}

interface EditProductForm {
  name: string;
  description: string;
  categoryIds: string[];
  price: string;
  promoPrice: string;
  unit: string;
  branchIds: string[];
  samePrice: boolean;
  branchPrices: Record<string, { price: string; promoPrice: string }>;
  branchStocks: Record<
    string,
    { stock: string; initialStock: number | null; unlimited: boolean }
  >;
}

type ProductGridSize = "small" | "medium" | "large";

interface OwnerProductPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

interface MediaPagination {
  page: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

const OWNER_PRODUCT_PAGE_SIZE = 60;
const INITIAL_OWNER_PRODUCT_PAGINATION: OwnerProductPagination = {
  page: 1,
  limit: OWNER_PRODUCT_PAGE_SIZE,
  total: 0,
  totalPages: 1,
  hasMore: false,
};
const INITIAL_MEDIA_PAGINATION: MediaPagination = {
  page: 1,
  total: 0,
  totalPages: 1,
  hasMore: false,
};
const MEDIA_PICKER_PAGE_SIZE = 50;

const blankProduct = (
  categoryIds: string[] = [],
  branchIds: string[] = [],
): NewProductForm => ({
  categoryIds,
  branchIds,
  name: "",
  description: "",
  price: "",
  promoPrice: "",
  unit: "",
  stock: "",
  unlimited: false,
});

const productSlug = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-|-$/gu, "");

function toMedia(payload: unknown): MediaOption[] {
  return normalizeCollection(payload)
    .items.map((item) => ({
      id: text(item.id),
      filename: text(item.filename, "Foto"),
    }))
    .filter((item) => item.id);
}

function toMediaPagination(payload: unknown): MediaPagination {
  const source =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as ApiRecord)
      : {};
  return {
    page: Math.max(1, number(value(source, "page"), 1)),
    total: Math.max(0, number(value(source, "total"), 0)),
    totalPages: Math.max(
      1,
      number(value(source, "totalPages", "total_pages"), 1),
    ),
    hasMore: bool(value(source, "hasMore", "has_more")),
  };
}

function toIdentifierList(item: unknown): string[] {
  if (Array.isArray(item)) {
    return item.map((entry) => text(entry)).filter(Boolean);
  }
  return typeof item === "string" ? item.split(",").filter(Boolean) : [];
}

function toProductRows(
  payload: unknown,
  categoryChoices: CategoryOption[],
  role: SessionUser["role"],
  user?: SessionUser,
): ProductRow[] {
  const categoryNamesFor = (ids: string[], fallback: string) => {
    const names = ids
      .map((id) => categoryChoices.find((item) => item.id === id)?.name)
      .filter((name): name is string => Boolean(name));
    return names.length > 0 ? names : [fallback];
  };
  const rows: ProductRow[] = [];
  for (const item of normalizeCollection(payload).items) {
    const baseCategoryId = text(value(item, "categoryId", "category_id"));
    const baseCategoryName = text(
      value(item, "categoryName", "category_name"),
      "Tanpa kategori",
    );
    const baseCategoryIds = toIdentifierList(
      value(item, "categoryIds", "category_ids"),
    );
  const base = {
      id: text(item.id),
      name: text(item.name),
      description: text(item.description),
      categoryId: baseCategoryId,
      categoryIds:
        baseCategoryIds.length > 0
          ? baseCategoryIds
          : baseCategoryId
            ? [baseCategoryId]
            : [],
      categoryName: baseCategoryName,
      categoryNames: categoryNamesFor(baseCategoryIds, baseCategoryName),
    };
    if (role === "OWNER") {
      for (const branch of Array.isArray(item.branches)
        ? (item.branches as ApiRecord[])
        : []) {
        const branchCategoryId = text(
          value(branch, "categoryId", "category_id"),
          base.categoryId,
        );
        const branchCategoryName = text(
          value(branch, "categoryName", "category_name"),
          base.categoryName,
        );
        const branchCategoryIds = toIdentifierList(
          value(branch, "categoryIds", "category_ids"),
        );
        rows.push({
          ...base,
          name: text(branch.name, base.name),
          description: text(branch.description, base.description),
          categoryId: branchCategoryId,
          categoryIds:
            branchCategoryIds.length > 0
              ? branchCategoryIds
              : branchCategoryId
                ? [branchCategoryId]
                : [],
          categoryName: branchCategoryName,
          categoryNames: categoryNamesFor(
            branchCategoryIds,
            branchCategoryName,
          ),
          branch: {
            branchId: text(value(branch, "branchId", "branch_id")),
            branchName: text(value(branch, "branchName", "branch_name")),
            price: number(branch.price),
            promoPrice: nullableNumber(branch, "promoPrice", "promo_price"),
            unit: text(branch.unit, text(item.unit, "pcs")),
            stock: number(branch.stock),
            minimumStock: number(
              value(branch, "minimumStock", "minimum_stock"),
            ),
            unlimited: bool(value(branch, "isUnlimited", "is_unlimited")),
            available: bool(value(branch, "available", "is_available")),
            featured: bool(value(branch, "featured", "is_featured")),
            mediaId: text(value(branch, "mediaId", "media_id")) || undefined,
            branchMediaId:
              text(value(branch, "branchMediaId", "branch_media_id")) ||
              undefined,
          },
        });
      }
    } else {
      rows.push({
        ...base,
        branch: {
          branchId: text(user?.branchId),
          branchName: user?.branchName ?? "Cabang Anda",
          price: number(item.price),
          promoPrice: nullableNumber(item, "promoPrice", "promo_price"),
          unit: text(item.unit, "pcs"),
          stock: number(value(item, "stock", "quantity")),
          minimumStock: number(
            value(item, "minimumStock", "minimum_stock"),
          ),
          unlimited: bool(value(item, "isUnlimited", "is_unlimited")),
          available: bool(value(item, "isAvailable", "is_available")),
          featured: bool(value(item, "isFeatured", "is_featured")),
          mediaId: text(value(item, "mediaId", "media_id")) || undefined,
          branchMediaId:
            text(value(item, "branchMediaId", "branch_media_id")) ||
            undefined,
        },
      });
    }
  }
  return rows;
}

function toOwnerProductPagination(payload: unknown): OwnerProductPagination {
  const source =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? (payload as ApiRecord)
      : {};
  const collection = normalizeCollection(payload);
  const limit = Math.max(
    1,
    number(source.limit, OWNER_PRODUCT_PAGE_SIZE),
  );
  const total = Math.max(0, number(source.total, collection.total));
  const totalPages = Math.max(
    1,
    number(source.totalPages ?? source.total_pages, Math.ceil(total / limit)),
  );
  const page = Math.min(
    totalPages,
    Math.max(1, number(source.page, 1)),
  );
  return {
    page,
    limit,
    total,
    totalPages,
    hasMore: bool(source.hasMore ?? source.has_more, page < totalPages),
  };
}

export function ProductCatalogManagementPage({
  role,
}: {
  role: SessionUser["role"];
}) {
  const { user } = useSession();
  const prefix = role === "OWNER" ? "/api/owner" : "/api/admin";
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [media, setMedia] = useState<MediaOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [branchChoices, setBranchChoices] = useState<BranchOption[]>([]);
  const [query, setQuery] = useState("");
  const [ownerProductPage, setOwnerProductPage] = useState(1);
  const [ownerProductPagination, setOwnerProductPagination] =
    useState<OwnerProductPagination>(INITIAL_OWNER_PRODUCT_PAGINATION);
  const [ownerProductBusy, setOwnerProductBusy] = useState(false);
  const [ownerCatalogInitialized, setOwnerCatalogInitialized] = useState(false);
  const ownerCatalogInitializedRef = useRef(false);
  const skipNextOwnerProductFetch = useRef(false);
  const [category, setCategory] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [stockThreshold, setStockThreshold] = useState("");
  const [gridSize, setGridSize] = useState<ProductGridSize>("large");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [setValues, setSetValues] = useState<Record<string, string>>({});
  const [editTarget, setEditTarget] = useState<ProductRow>();
  const [editForm, setEditForm] = useState<EditProductForm>({
    name: "",
    description: "",
    categoryIds: [],
    price: "",
    promoPrice: "",
    unit: "",
    branchIds: [],
    samePrice: true,
    branchPrices: {},
    branchStocks: {},
  });
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [bulkCategoryIds, setBulkCategoryIds] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [categoryImageModal, setCategoryImageModal] = useState(false);
  const [categoryImageCategoryId, setCategoryImageCategoryId] = useState("");
  const [categoryImageMediaId, setCategoryImageMediaId] = useState("");
  const [categoryImageOptimized, setCategoryImageOptimized] =
    useState<OptimizedImage>();
  const [photoTarget, setPhotoTarget] = useState<ProductRow>();
  const [selectedMediaId, setSelectedMediaId] = useState("");
  const [mediaQuery, setMediaQuery] = useState("");
  const [mediaPagination, setMediaPagination] = useState<MediaPagination>(
    INITIAL_MEDIA_PAGINATION,
  );
  const [mediaBusy, setMediaBusy] = useState(false);
  const [sharePhotoAcrossBranches, setSharePhotoAcrossBranches] =
    useState(false);
  const mediaRequestSequence = useRef(0);
  const [optimized, setOptimized] = useState<OptimizedImage>();
  const [categoryModal, setCategoryModal] = useState(false);
  const [categoryEditId, setCategoryEditId] = useState<string>();
  const [categoryDeleteTarget, setCategoryDeleteTarget] =
    useState<CategoryOption>();
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    slug: "",
    description: "",
  });
  const [productModal, setProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState<NewProductForm>(blankProduct());
  const [newProductMediaId, setNewProductMediaId] = useState("");
  const [newProductOptimized, setNewProductOptimized] =
    useState<OptimizedImage>();
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [deleteProductTarget, setDeleteProductTarget] =
    useState<ProductRow>();
  const [navigationBranchId, setNavigationBranchId] = useState("");
  const [categoryNavigation, setCategoryNavigation] = useState<
    Record<string, string>
  >({});
  const [navigationBusy, setNavigationBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [productPayload, mediaPayload, categoryPayload, branchPayload] = await Promise.all(
        [
          apiRequest<unknown>(
            `${prefix}/products${
              role === "OWNER"
                ? queryString({ page: 1, limit: OWNER_PRODUCT_PAGE_SIZE })
                : ""
            }`,
          ),
          apiRequest<unknown>(
            `${prefix}/media${queryString({
              page: 1,
              limit: MEDIA_PICKER_PAGE_SIZE,
              status: "active",
              type: "product",
            })}`,
          ),
          apiRequest<unknown>(`${prefix}/categories`),
          role === "OWNER"
            ? apiRequest<unknown>("/api/owner/branches")
            : Promise.resolve([]),
        ],
      );
      const categoryChoices = normalizeCollection(categoryPayload)
        .items.map((item) => ({
          id: text(item.id),
          name: text(item.name),
          slug: text(item.slug),
          description: text(item.description),
          mediaId: text(value(item, "mediaId", "media_id")) || undefined,
          selectedMediaId:
            text(value(item, "selectedMediaId", "selected_media_id")) ||
            undefined,
          navigationOrder:
            value(item, "navigationOrder", "navigation_order") === undefined ||
            value(item, "navigationOrder", "navigation_order") === null
              ? undefined
              : number(value(item, "navigationOrder", "navigation_order")),
        }))
        .filter((item) => item.id && item.name);
      setProducts(toProductRows(productPayload, categoryChoices, role, user));
      if (role === "OWNER") {
        setOwnerProductPage(1);
        setOwnerProductPagination(toOwnerProductPagination(productPayload));
        skipNextOwnerProductFetch.current = !ownerCatalogInitializedRef.current;
        ownerCatalogInitializedRef.current = true;
        setOwnerCatalogInitialized(true);
      }
      setMedia(toMedia(mediaPayload));
      setMediaPagination(toMediaPagination(mediaPayload));
      setCategoryOptions(categoryChoices);
      const nextBranches =
        role === "OWNER"
          ? normalizeCollection(branchPayload).items
              .map((item) => ({ id: text(item.id), name: text(item.name) }))
              .filter((item) => item.id && item.name)
          : user?.branchId
            ? [
                {
                  id: String(user.branchId),
                  name: user.branchName ?? "Cabang Anda",
                },
              ]
            : [];
      setBranchChoices(nextBranches);
      setNavigationBranchId((current) => current || nextBranches[0]?.id || "");
      if (role === "ADMIN") {
        setCategoryNavigation(
          Object.fromEntries(
            categoryChoices
              .filter((item) => item.navigationOrder !== undefined)
              .map((item) => [item.id, String(item.navigationOrder)]),
          ),
        );
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Katalog belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [prefix, role, user]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  useEffect(() => {
    if (!photoTarget && !productModal && !categoryImageModal) return;
    const requestId = ++mediaRequestSequence.current;
    const timer = window.setTimeout(() => {
      setMediaBusy(true);
      void apiRequest<unknown>(
        `${prefix}/media${queryString({
          page: 1,
          limit: MEDIA_PICKER_PAGE_SIZE,
          search: mediaQuery.trim() || undefined,
          status: "active",
          type: photoTarget || productModal ? "product" : undefined,
        })}`,
      )
        .then((payload) => {
          if (requestId !== mediaRequestSequence.current) return;
          setMedia(toMedia(payload));
          setMediaPagination(toMediaPagination(payload));
        })
        .catch((caught) => {
          if (requestId !== mediaRequestSequence.current) return;
          setNotice(
            caught instanceof Error
              ? caught.message
              : "Daftar foto belum dapat dimuat.",
          );
        })
        .finally(() => {
          if (requestId === mediaRequestSequence.current) setMediaBusy(false);
        });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [categoryImageModal, mediaQuery, photoTarget, prefix, productModal]);

  const loadMoreMedia = async () => {
    if (mediaBusy || !mediaPagination.hasMore) return;
    const requestId = ++mediaRequestSequence.current;
    const nextPage = mediaPagination.page + 1;
    setMediaBusy(true);
    try {
      const payload = await apiRequest<unknown>(
        `${prefix}/media${queryString({
          page: nextPage,
          limit: MEDIA_PICKER_PAGE_SIZE,
          search: mediaQuery.trim() || undefined,
          status: "active",
          type: photoTarget || productModal ? "product" : undefined,
        })}`,
      );
      if (requestId !== mediaRequestSequence.current) return;
      const nextItems = toMedia(payload);
      setMedia((current) => {
        const byId = new Map(current.map((item) => [item.id, item]));
        nextItems.forEach((item) => byId.set(item.id, item));
        return [...byId.values()];
      });
      setMediaPagination(toMediaPagination(payload));
    } catch (caught) {
      if (requestId !== mediaRequestSequence.current) return;
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Foto berikutnya belum dapat dimuat.",
      );
    } finally {
      if (requestId === mediaRequestSequence.current) setMediaBusy(false);
    }
  };
  useEffect(() => {
    if (role !== "OWNER" || !ownerCatalogInitialized) {
      return;
    }
    if (skipNextOwnerProductFetch.current) {
      skipNextOwnerProductFetch.current = false;
      return;
    }
    let active = true;
    setOwnerProductBusy(true);
    const timer = window.setTimeout(() => {
      const selectedCategoryId = categoryOptions.find(
        (item) => item.name === category,
      )?.id;
      const parsedStockMaximum = stockThreshold.trim()
        ? Number(stockThreshold)
        : undefined;
      void apiRequest<unknown>(
        `/api/owner/products${queryString({
          page: ownerProductPage,
          limit: OWNER_PRODUCT_PAGE_SIZE,
          search: query.trim() || undefined,
          branch_id: branchFilter || undefined,
          category_id: selectedCategoryId,
          stock_max:
            parsedStockMaximum !== undefined &&
            Number.isInteger(parsedStockMaximum) &&
            parsedStockMaximum >= 0
              ? parsedStockMaximum
              : undefined,
        })}`,
      )
        .then((payload) => {
          if (active) {
            const pagination = toOwnerProductPagination(payload);
            setProducts(toProductRows(payload, categoryOptions, role, user));
            setOwnerProductPagination(pagination);
            if (pagination.page !== ownerProductPage) {
              setOwnerProductPage(pagination.page);
            }
          }
        })
        .catch((caught) => {
          if (active) {
            setNotice(
              caught instanceof Error
                ? caught.message
                : "Pencarian produk belum dapat dimuat.",
            );
          }
        })
        .finally(() => {
          if (active) setOwnerProductBusy(false);
        });
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    branchFilter,
    category,
    categoryOptions,
    ownerCatalogInitialized,
    ownerProductPage,
    query,
    role,
    stockThreshold,
    user,
  ]);
  useEffect(() => {
    if (!navigationBranchId || role !== "OWNER") return;
    let active = true;
    void apiRequest<unknown>(
      `/api/owner/category-navigation?branch_id=${encodeURIComponent(navigationBranchId)}`,
    )
      .then((payload) => {
        if (!active) return;
        setCategoryNavigation(
          Object.fromEntries(
            normalizeCollection(payload).items.map((item) => [
              text(value(item, "categoryId", "category_id")),
              String(number(value(item, "navigationOrder", "navigation_order"))),
            ]),
          ),
        );
      })
      .catch((caught) => {
        if (active) {
          setNotice(
            caught instanceof Error
              ? caught.message
              : "Urutan kategori belum dapat dimuat.",
          );
        }
      });
    return () => {
      active = false;
    };
  }, [categoryOptions, navigationBranchId, role]);

  const saveCategoryNavigation = async () => {
    if (!navigationBranchId) return;
    const items = Object.entries(categoryNavigation)
      .filter(([, order]) => order !== "")
      .map(([categoryId, order]) => ({
        categoryId,
        navigationOrder: Number(order),
      }));
    setNavigationBusy(true);
    setNotice(undefined);
    try {
      await apiRequest(`${prefix}/category-navigation`, {
        method: "PUT",
        ...jsonBody(
          role === "OWNER"
            ? { branchId: navigationBranchId, items }
            : { items },
        ),
      });
      setNotice(
        `${items.length} kategori menu atas berhasil disimpan untuk cabang terpilih.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Urutan kategori belum berhasil disimpan.",
      );
    } finally {
      setNavigationBusy(false);
    }
  };
  const keyFor = (row: ProductRow) => `${row.id}:${row.branch.branchId}`;
  const categories = useMemo(
    () => categoryOptions.map((item) => item.name),
    [categoryOptions],
  );
  const selectedCategoryOption = useMemo(
    () => categoryOptions.find((item) => item.name === category),
    [category, categoryOptions],
  );
  const filteredMedia = media;
  const branchOptions = branchChoices;
  const visible = useMemo(
    () => {
      const threshold = stockThreshold.trim() === ""
        ? undefined
        : Number(stockThreshold);
      return products.filter(
        (row) =>
          (!branchFilter || row.branch.branchId === branchFilter) &&
          (!category || row.categoryNames.includes(category)) &&
          (threshold === undefined ||
            (!row.branch.unlimited &&
              Number.isFinite(threshold) &&
              row.branch.stock <= threshold)) &&
          (role === "OWNER" ||
            `${row.name} ${row.branch.branchName}`
              .toLowerCase()
              .includes(query.toLowerCase())),
      );
    },
    [
      products,
      query,
      category,
      branchFilter,
      stockThreshold,
      role,
    ],
  );

  const endpointFor = (row: ProductRow) =>
    role === "OWNER"
      ? `/api/owner/products/${encodeURIComponent(row.id)}/branch/${encodeURIComponent(row.branch.branchId)}`
      : `/api/admin/products/${encodeURIComponent(row.id)}`;

  const openEditProduct = (row: ProductRow) => {
    const productRows = products.filter((item) => item.id === row.id);
    const first = productRows[0]?.branch;
    const pricesAreSame = productRows.every(
      (item) =>
        item.branch.price === first?.price &&
        item.branch.promoPrice === first?.promoPrice,
    );
    setEditTarget(row);
    setEditForm({
      name: row.name,
      description: row.description,
      categoryIds: row.categoryIds,
      price: String(row.branch.price),
      promoPrice:
        row.branch.promoPrice === null ? "" : String(row.branch.promoPrice),
      unit: row.branch.unit,
      branchIds: productRows.map((item) => item.branch.branchId),
      samePrice: pricesAreSame,
      branchPrices: Object.fromEntries(
        productRows.map((item) => [
          item.branch.branchId,
          {
            price: String(item.branch.price),
            promoPrice:
              item.branch.promoPrice === null
                ? ""
                : String(item.branch.promoPrice),
          },
        ]),
      ),
      branchStocks: Object.fromEntries(
        productRows.map((item) => [
          item.branch.branchId,
          {
            stock: String(item.branch.stock),
            initialStock: item.branch.stock,
            unlimited: item.branch.unlimited,
          },
        ]),
      ),
    });
  };

  const saveProduct = async (event: FormEvent) => {
    event.preventDefault();
    if (!editTarget) return;
    const price = Number(editForm.price);
    const promoPrice = editForm.promoPrice.trim()
      ? Number(editForm.promoPrice)
      : null;
    if (
      !editForm.name.trim() ||
      !Number.isInteger(price) ||
      price < 0 ||
      (promoPrice !== null &&
        (!Number.isInteger(promoPrice) ||
          promoPrice < 0 ||
          promoPrice >= price))
    ) {
      setNotice(
        "Periksa harga. Harga promo harus kosong atau lebih rendah dari harga normal.",
      );
      return;
    }
    if (role === "OWNER" && editForm.branchIds.length === 0) {
      setNotice("Pilih minimal satu cabang untuk produk ini.");
      return;
    }
    if (
      role === "OWNER" &&
      editForm.branchIds.some(
        (branchId) => !branchOptions.some((branch) => branch.id === branchId),
      )
    ) {
      setNotice(
        "Pilihan cabang sudah berubah. Tekan Perbarui lalu pilih cabang kembali.",
      );
      return;
    }
    const ownerBranches = editForm.branchIds.map((branchId) => {
      const existing = products.find(
        (row) => row.id === editTarget.id && row.branch.branchId === branchId,
      );
      const branchStock = editForm.branchStocks[branchId] ?? {
        stock: "",
        initialStock: null,
        unlimited: existing?.branch.unlimited ?? false,
      };
      const enteredStock = branchStock.stock.trim();
      const stock =
        !branchStock.unlimited && enteredStock !== ""
          ? Number(enteredStock)
          : undefined;
      const branchPrice = editForm.samePrice
        ? { price: editForm.price, promoPrice: editForm.promoPrice }
        : (editForm.branchPrices[branchId] ?? {
            price: editForm.price,
            promoPrice: editForm.promoPrice,
          });
      return {
        branchId,
        price: Number(branchPrice.price),
        promoPrice: branchPrice.promoPrice.trim()
          ? Number(branchPrice.promoPrice)
          : null,
        isAvailable: existing?.branch.available ?? true,
        isFeatured: existing?.branch.featured ?? false,
        isUnlimited: branchStock.unlimited,
        stock:
          stock !== undefined &&
          (branchStock.initialStock === null || stock !== branchStock.initialStock)
            ? stock
            : undefined,
      };
    });
    if (
      ownerBranches.some(
        (branch) =>
          !Number.isInteger(branch.price) ||
          branch.price < 0 ||
          (branch.stock !== undefined &&
            (!Number.isInteger(branch.stock) ||
              branch.stock < 0 ||
              branch.stock > 1_000_000)) ||
          (branch.promoPrice !== null &&
            (!Number.isInteger(branch.promoPrice) ||
              branch.promoPrice < 0 ||
              branch.promoPrice >= branch.price)),
      )
    ) {
      setNotice(
        "Periksa harga dan stok setiap cabang. Stok harus 0 sampai 1.000.000, dan harga promo harus lebih rendah dari harga normal.",
      );
      return;
    }
    const key = keyFor(editTarget);
    setBusyId(key);
    setNotice(undefined);
    try {
      if (role === "OWNER") {
        await apiRequest(
          `/api/owner/products/${encodeURIComponent(editTarget.id)}/branches`,
          {
            method: "PUT",
            ...jsonBody({
              name: editForm.name.trim(),
              description: editForm.description.trim(),
              categoryIds: editForm.categoryIds,
              unit: editForm.unit.trim(),
              branches: ownerBranches,
            }),
          },
        );
      } else {
        await apiRequest(endpointFor(editTarget), {
          method: "PATCH",
          ...jsonBody({
            name: editForm.name.trim(),
            description: editForm.description.trim(),
            categoryIds: editForm.categoryIds,
            price,
            promoPrice,
            unit: editForm.unit.trim(),
            isAvailable: editTarget.branch.available,
            isFeatured: editTarget.branch.featured,
            isUnlimited: editTarget.branch.unlimited,
          }),
        });
      }
      setEditTarget(undefined);
      setNotice(
        role === "OWNER"
          ? `Produk berhasil diterapkan ke ${ownerBranches.length} cabang tanpa mencampur stok.`
          : "Nama, deskripsi, kategori, harga, promo, dan satuan produk cabang berhasil diperbarui.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Produk belum berhasil diperbarui.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const moveSelectedProducts = async () => {
    if (role !== "ADMIN" || selectedProductIds.length === 0) return;
    setBulkBusy(true);
    setNotice(undefined);
    try {
      await apiRequest("/api/admin/products/bulk-category", {
        method: "POST",
        ...jsonBody({
          productIds: selectedProductIds,
          categoryIds: bulkCategoryIds,
        }),
      });
      setNotice(
        bulkCategoryIds.length > 0
          ? `${selectedProductIds.length} produk berhasil digabungkan ke ${bulkCategoryIds.length} kategori terpilih.`
          : `${selectedProductIds.length} produk dikembalikan ke kategori awalnya.`,
      );
      setSelectedProductIds([]);
      setBulkCategoryIds([]);
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Produk terpilih belum berhasil dipindahkan.",
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const updateAvailability = async (row: ProductRow, available: boolean) => {
    const key = keyFor(row);
    setBusyId(key);
    setNotice(undefined);
    try {
      await apiRequest(endpointFor(row), {
        method: "PATCH",
        ...jsonBody({
          price: row.branch.price,
          promoPrice: row.branch.promoPrice,
          unit: row.branch.unit,
          isAvailable: available,
          isFeatured: row.branch.featured,
          isUnlimited: row.branch.unlimited,
        }),
      });
      setNotice(`${row.name} sekarang ${available ? "tersedia" : "habis"}.`);
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Ketersediaan belum berhasil diperbarui.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const updateUnlimited = async (row: ProductRow, unlimited: boolean) => {
    const key = keyFor(row);
    setBusyId(key);
    setNotice(undefined);
    try {
      await apiRequest(endpointFor(row), {
        method: "PATCH",
        ...jsonBody({
          name: row.name,
          categoryIds: row.categoryIds,
          price: row.branch.price,
          promoPrice: row.branch.promoPrice,
          unit: row.branch.unit,
          isAvailable: true,
          isFeatured: row.branch.featured,
          isUnlimited: unlimited,
        }),
      });
      setNotice(
        unlimited
          ? `${row.name} sekarang memakai stok Unlimited di ${row.branch.branchName}.`
          : `${row.name} kembali memakai jumlah stok biasa di ${row.branch.branchName}.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Pengaturan Unlimited belum berhasil disimpan.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const deleteProduct = async () => {
    if (!deleteProductTarget) return;
    const row = deleteProductTarget;
    const key = keyFor(row);
    setBusyId(key);
    setNotice(undefined);
    try {
      const endpoint =
        role === "OWNER"
          ? `/api/owner/products/${encodeURIComponent(row.id)}/branch/${encodeURIComponent(row.branch.branchId)}`
          : `/api/admin/products/${encodeURIComponent(row.id)}`;
      await apiRequest(endpoint, {
        method: "DELETE",
        ...jsonBody({}),
      });
      setDeleteProductTarget(undefined);
      setNotice(
        `${row.name} dihapus dari katalog ${row.branch.branchName}. Riwayat pesanan tetap aman.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Produk belum berhasil dihapus.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const adjust = async (
    row: ProductRow,
    type: "ADD" | "SUBTRACT" | "SET",
    quantity: number,
  ): Promise<boolean> => {
    const key = keyFor(row);
    setBusyId(key);
    setNotice(undefined);
    try {
      const endpoint =
        role === "OWNER"
          ? `/api/owner/stock/${encodeURIComponent(row.branch.branchId)}/${encodeURIComponent(row.id)}/adjust`
          : `/api/admin/stock/${encodeURIComponent(row.id)}/adjust`;
      const result = await apiRequest<{
        branchId: string;
        branchName: string;
        productId: string;
        productName: string;
        quantityBefore: number;
        quantityAfter: number;
        quantityChange: number;
      }>(endpoint, {
        method: "POST",
        ...jsonBody({ type, quantity, note: "Penyesuaian dari kartu produk" }),
      });
      setProducts((current) =>
        current.map((candidate) =>
          keyFor(candidate) === key
            ? {
                ...candidate,
                branch: {
                  ...candidate.branch,
                  stock: result.quantityAfter,
                },
              }
            : candidate,
        ),
      );
      setSetValues((current) => ({ ...current, [key]: "" }));
      setNotice(
        `Stok ${result.productName} di ${result.branchName}: ${result.quantityBefore} menjadi ${result.quantityAfter}.`,
      );
      return true;
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Stok belum berhasil diperbarui.",
      );
      return false;
    } finally {
      setBusyId(undefined);
    }
  };

  const attachMedia = async (mediaId: string) => {
    if (!photoTarget) return;
    const key = keyFor(photoTarget);
    setBusyId(key);
    try {
      await apiRequest(
        `${prefix}/products/${encodeURIComponent(photoTarget.id)}/media`,
        {
          method: "PATCH",
          ...jsonBody({
            mediaId: mediaId || null,
            ...(role === "OWNER"
              ? sharePhotoAcrossBranches
                ? { branchId: null, shareAcrossBranches: true }
                : {
                    branchId: photoTarget.branch.branchId,
                    shareAcrossBranches: false,
                  }
              : {}),
          }),
        },
      );
      setPhotoTarget(undefined);
      setOptimized(undefined);
      setSelectedMediaId("");
      setMediaQuery("");
      setSharePhotoAcrossBranches(false);
      setNotice(
        role === "OWNER" && sharePhotoAcrossBranches
          ? "Foto default semua cabang berhasil diperbarui tanpa menyalin file."
          : "Foto produk berhasil diperbarui.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Foto belum berhasil dipasang.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const chooseFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setOptimized(await optimizeProductImage(file));
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Foto belum dapat diproses.",
      );
    }
  };
  const uploadAndAttach = async () => {
    if (!optimized || !photoTarget) return;
    const uploaded = await apiRequest<{ id: string }>("/api/media", {
      method: "POST",
      ...jsonBody({
        filename: optimized.filename,
        originalFilename: optimized.filename,
        mediaType: "product",
        altText: `Foto ${photoTarget.name}`,
        dataBase64: await blobToBase64(optimized.blob),
      }),
    });
    await attachMedia(uploaded.id);
  };

  const openProductModal = () => {
    const selectedCategory = categoryOptions.find(
      (item) => item.name === category,
    );
    setNewProduct(
      blankProduct(
        selectedCategory?.id
          ? [String(selectedCategory.id)]
          : categoryOptions[0]?.id
            ? [String(categoryOptions[0].id)]
            : [],
        role === "OWNER"
          ? branchFilter
            ? [String(branchFilter)]
            : branchOptions[0]?.id
              ? [String(branchOptions[0].id)]
              : []
          : user?.branchId
            ? [String(user.branchId)]
            : [],
      ),
    );
    setNewProductMediaId("");
    setNewProductOptimized(undefined);
    setMediaQuery("");
    setProductModal(true);
  };

  const chooseNewProductFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setNewProductOptimized(await optimizeProductImage(file));
      setNewProductMediaId("");
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Foto belum dapat diproses.",
      );
    }
  };

  const createProduct = async (event: FormEvent) => {
    event.preventDefault();
    const price = Number(newProduct.price);
    const promoPrice = newProduct.promoPrice.trim()
      ? Number(newProduct.promoPrice)
      : null;
    const stock = Number(newProduct.stock);
    if (
      newProduct.categoryIds.length === 0 ||
      newProduct.branchIds.length === 0 ||
      !newProduct.name.trim() ||
      !newProduct.unit.trim() ||
      !Number.isInteger(price) ||
      price < 0 ||
      (!newProduct.unlimited && (!Number.isInteger(stock) || stock < 0)) ||
      (promoPrice !== null &&
        (!Number.isInteger(promoPrice) ||
          promoPrice < 0 ||
          promoPrice >= price))
    ) {
      setNotice(
        "Lengkapi data produk. Harga promo harus kosong atau lebih rendah dari harga normal.",
      );
      return;
    }
    if (
      role === "OWNER" &&
      newProduct.branchIds.some(
        (branchId) => !branchOptions.some((branch) => branch.id === branchId),
      )
    ) {
      setNotice(
        "Pilihan cabang sudah berubah. Tekan Perbarui lalu pilih cabang kembali.",
      );
      return;
    }
    setCreatingProduct(true);
    setNotice(undefined);
    try {
      let mediaId = newProductMediaId || null;
      if (newProductOptimized) {
        const uploaded = await apiRequest<{ id: string }>("/api/media", {
          method: "POST",
          ...jsonBody({
            filename: newProductOptimized.filename,
            originalFilename: newProductOptimized.filename,
            mediaType: "product",
            altText: `Foto ${newProduct.name.trim()}`,
            dataBase64: await blobToBase64(newProductOptimized.blob),
          }),
        });
        mediaId = uploaded.id;
      }
      const categoryId = newProduct.categoryIds[0]!;
      const common = {
        categoryId,
        categoryIds: newProduct.categoryIds,
        name: newProduct.name.trim(),
        description: newProduct.description.trim() || null,
        price,
        promoPrice,
        unit: newProduct.unit.trim(),
        stock: newProduct.unlimited ? 0 : stock,
        isUnlimited: newProduct.unlimited,
        minimumStock: 5,
        mediaId,
      };
      await apiRequest(`${prefix}/products`, {
        method: "POST",
        ...jsonBody(
          role === "OWNER"
            ? {
                ...common,
                slug: productSlug(newProduct.name),
                branches: newProduct.branchIds.map((branchId) => ({
                  branchId,
                  price,
                  promoPrice,
                  stock: newProduct.unlimited ? 0 : stock,
                  minimumStock: 5,
                  isUnlimited: newProduct.unlimited,
                  isAvailable: true,
                  isFeatured: false,
                })),
              }
            : common,
        ),
      });
      setProductModal(false);
      setNewProduct(blankProduct());
      setNewProductMediaId("");
      setNewProductOptimized(undefined);
      setNotice(
        role === "OWNER"
          ? "Produk berhasil ditambahkan ke cabang yang dipilih."
          : "Produk berhasil ditambahkan ke cabang ini dan langsung terlihat oleh Owner.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Produk belum berhasil ditambahkan.",
      );
    } finally {
      setCreatingProduct(false);
    }
  };

  const openCreateCategory = () => {
    setCategoryEditId(undefined);
    setCategoryForm({ name: "", slug: "", description: "" });
    setCategoryModal(true);
  };

  const openEditCategory = (item: CategoryOption) => {
    setCategoryEditId(item.id);
    setCategoryForm({
      name: item.name,
      slug: item.slug,
      description: item.description,
    });
    setCategoryModal(true);
  };

  const saveCategory = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(undefined);
    try {
      await apiRequest(
        categoryEditId
          ? `${prefix}/categories/${encodeURIComponent(categoryEditId)}`
          : `${prefix}/categories`,
        {
        method: categoryEditId ? "PATCH" : "POST",
        ...jsonBody({
          name: categoryForm.name,
          slug: categoryForm.slug,
          description: categoryForm.description || null,
          ...(role === "OWNER" && !categoryEditId
            ? {
                mediaId: null,
                displayOrder: categories.length + 1,
                isActive: true,
              }
            : {}),
        }),
      });
      setCategoryModal(false);
      setCategoryEditId(undefined);
      setCategoryForm({ name: "", slug: "", description: "" });
      setNotice(
        categoryEditId
          ? "Kategori berhasil diperbarui di seluruh tampilan terkait."
          : "Kategori berhasil ditambahkan dan langsung tersedia di main website.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Kategori belum berhasil dibuat.",
      );
    }
  };

  const deleteCategory = async () => {
    if (!categoryDeleteTarget) return;
    setBulkBusy(true);
    setNotice(undefined);
    try {
      const result = await apiRequest<{ movedProducts?: number }>(
        `${prefix}/categories/${encodeURIComponent(categoryDeleteTarget.id)}`,
        { method: "DELETE", ...jsonBody({}) },
      );
      if (category === categoryDeleteTarget.name) setCategory("");
      setCategoryDeleteTarget(undefined);
      setNotice(
        `Kategori berhasil dihapus. ${result.movedProducts ?? 0} produk dipindahkan ke Other.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Kategori belum berhasil dihapus.",
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const openCategoryImage = () => {
    const selected =
      categoryOptions.find((item) => item.name === category) ??
      categoryOptions[0];
    setCategoryImageCategoryId(selected?.id ?? "");
    setCategoryImageMediaId(selected?.selectedMediaId ?? "");
    setCategoryImageOptimized(undefined);
    setMediaQuery("");
    setCategoryImageModal(true);
  };

  const chooseCategoryImageFile = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setCategoryImageOptimized(await optimizeProductImage(file));
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Gambar kategori belum dapat diproses.",
      );
    }
  };

  const uploadCategoryImage = async () => {
    if (!categoryImageOptimized || !categoryImageCategoryId) return;
    setBulkBusy(true);
    setNotice(undefined);
    try {
      const categoryName =
        categoryOptions.find((item) => item.id === categoryImageCategoryId)
          ?.name ?? "kategori";
      const uploaded = await apiRequest<{ id: string }>("/api/media", {
        method: "POST",
        ...jsonBody({
          filename: categoryImageOptimized.filename,
          originalFilename: categoryImageOptimized.filename,
          mediaType: "category",
          altText: `Gambar kategori ${categoryName}`,
          dataBase64: await blobToBase64(categoryImageOptimized.blob),
        }),
      });
      setCategoryImageMediaId(uploaded.id);
      setMedia((current) => [
        { id: uploaded.id, filename: categoryImageOptimized.filename },
        ...current.filter((item) => item.id !== uploaded.id),
      ]);
      setCategoryImageOptimized(undefined);
      setNotice("Gambar baru berhasil diunggah. Tekan Simpan Gambar Kategori untuk memakainya.");
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Gambar kategori belum berhasil diunggah.",
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const saveCategoryImage = async () => {
    if (role !== "ADMIN" || !categoryImageCategoryId) return;
    setBulkBusy(true);
    setNotice(undefined);
    try {
      await apiRequest(
        `/api/admin/categories/${encodeURIComponent(categoryImageCategoryId)}/media`,
        {
          method: "PATCH",
          ...jsonBody({ mediaId: categoryImageMediaId || null }),
        },
      );
      setCategoryImageModal(false);
      setNotice(
        categoryImageMediaId
          ? "Gambar kategori cabang berhasil diperbarui."
          : "Gambar kategori kembali otomatis memakai foto produk pertama.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Gambar kategori belum berhasil diperbarui.",
      );
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Katalog visual</span>
          <h1>Produk, Harga, Foto & Stok</h1>
          <p>
            Filter kategori, ubah harga/promo/satuan, pilih foto secara visual,
            dan atur stok dari kartu.
          </p>
        </div>
        <div className="heading-actions">
          <Button
            variant="secondary"
            onClick={() => void load()}
            icon={<RefreshCw />}
          >
            Perbarui
          </Button>
          <Button
            variant="secondary"
            onClick={openCreateCategory}
            icon={<Plus />}
          >
            Tambah Kategori
          </Button>
        </div>
      </div>
      <section className="category-navigation-editor">
        <div>
          <span className="eyebrow">Menu website utama</span>
          <h2>Urutan kategori di atas Beranda</h2>
          <p>
            Pilih maksimal 8 kategori. Tanpa nomor berarti tidak tampil di menu
            atas. Beranda selalu pertama; Cek Pesanan dan Member selalu di akhir.
          </p>
        </div>
        {role === "OWNER" && (
          <label>
            Cabang
            <select
              value={navigationBranchId}
              onChange={(event) => setNavigationBranchId(event.target.value)}
            >
              {branchOptions.map((branch) => (
                <option value={branch.id} key={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="category-navigation-grid">
          {categoryOptions.map((item) => (
            <label key={item.id}>
              <span>{item.name}</span>
              <select
                value={categoryNavigation[item.id] ?? ""}
                onChange={(event) => {
                  const nextOrder = event.target.value;
                  setCategoryNavigation((current) => {
                    const next = { ...current };
                    if (nextOrder) {
                      for (const [categoryId, order] of Object.entries(next)) {
                        if (categoryId !== item.id && order === nextOrder) {
                          next[categoryId] = "";
                        }
                      }
                    }
                    next[item.id] = nextOrder;
                    return next;
                  });
                }}
              >
                <option value="">Tidak tampil</option>
                {Array.from({ length: 8 }, (_, index) => index + 1).map(
                  (order) => (
                    <option value={order} key={order}>
                      Nomor {order}
                    </option>
                  ),
                )}
              </select>
            </label>
          ))}
        </div>
        <Button
          onClick={() => void saveCategoryNavigation()}
          busy={navigationBusy}
          icon={<Save />}
        >
          Simpan Menu Kategori
        </Button>
      </section>
      {role === "ADMIN" && (
        <Notice tone="info">
          Admin Cabang · {user?.branchName} 🔒 Nama, kategori, harga, promo,
          satuan, foto, dan stok hanya berlaku untuk cabang ini.
        </Notice>
      )}
      {role === "OWNER" && (
        <Notice tone="info">
          Stok setiap cabang berdiri sendiri. Pilih cabang di filter sebelum
          menambah atau mengurangi stok; perubahan hanya mengenai kartu cabang
          tersebut.
        </Notice>
      )}
      {notice && (
        <Notice
          tone={
            notice.includes("berhasil") || notice.includes("sekarang")
              ? "success"
              : "danger"
          }
        >
          {notice}
        </Notice>
      )}
      <div className="toolbar">
        <label className="dashboard-search">
          <Search />
          <span className="sr-only">Cari produk</span>
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              if (role === "OWNER") setOwnerProductPage(1);
            }}
            placeholder="Cari produk atau cabang…"
          />
        </label>
        <label className="toolbar-filter">
          <Filter />
          <span className="sr-only">Filter kategori</span>
          <select
            value={category}
            onChange={(event) => {
              setCategory(event.target.value);
              if (role === "OWNER") setOwnerProductPage(1);
            }}
          >
            <option value="">Semua kategori</option>
            {categories.map((name) => (
              <option value={name} key={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        {role === "OWNER" && (
          <label className="toolbar-filter">
            <Filter />
            <span className="sr-only">Filter cabang stok</span>
            <select
              value={branchFilter}
              onChange={(event) => {
                setBranchFilter(event.target.value);
                setOwnerProductPage(1);
              }}
            >
              <option value="">Semua cabang</option>
              {branchOptions.map((branch) => (
                <option value={branch.id} key={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="stock-threshold-filter">
          <Warehouse aria-hidden="true" />
          <span>Stok maksimal</span>
          <input
            type="number"
            min="0"
            inputMode="numeric"
            value={stockThreshold}
            placeholder="Contoh: 5"
            aria-label="Tampilkan produk dengan stok di bawah atau sama dengan"
            onChange={(event) => {
              setStockThreshold(event.target.value);
              if (role === "OWNER") setOwnerProductPage(1);
            }}
          />
        </label>
        <div className="product-grid-size" aria-label="Ukuran kartu produk">
          <span>Ukuran kartu</span>
          {(["small", "medium", "large"] as ProductGridSize[]).map(
            (size) => (
              <button
                type="button"
                className={gridSize === size ? "active" : ""}
                aria-pressed={gridSize === size}
                onClick={() => setGridSize(size)}
                key={size}
              >
                {size === "small"
                  ? "Kecil · 9"
                  : size === "medium"
                    ? "Sedang · 6"
                    : "Besar · 3"}
              </button>
            ),
          )}
        </div>
        {selectedCategoryOption && selectedCategoryOption.slug !== "other" && (
          <>
            <Button
              variant="secondary"
              onClick={() => openEditCategory(selectedCategoryOption)}
              icon={<Pencil />}
            >
              Ubah Kategori
            </Button>
            <Button
              variant="danger"
              onClick={() => setCategoryDeleteTarget(selectedCategoryOption)}
              icon={<Trash2 />}
            >
              Hapus Kategori
            </Button>
          </>
        )}
        {role === "ADMIN" && (
            <Button
              variant="secondary"
              onClick={openCategoryImage}
              icon={<ImagePlus />}
              disabled={categoryOptions.length === 0}
            >
              Gambar Kategori
            </Button>
        )}
        <Button
          onClick={openProductModal}
          icon={<Plus />}
          disabled={categoryOptions.length === 0 || branchOptions.length === 0}
        >
          {category ? `Tambah di ${category}` : "Tambah Produk"}
        </Button>
      </div>
      {role === "ADMIN" && (
        <section
          className="bulk-product-bar"
          aria-label="Pindahkan beberapa produk"
        >
          <div>
            <strong>{selectedProductIds.length} produk dipilih</strong>
            <span>
              Pilih satu atau beberapa kategori. Kosongkan semua pilihan untuk
              mengembalikan produk ke kategori awalnya.
            </span>
          </div>
          <div className="category-check-grid" aria-label="Kategori tujuan">
            {categoryOptions.map((item) => (
              <label key={item.id}>
                <input
                  type="checkbox"
                  checked={bulkCategoryIds.includes(item.id)}
                  onChange={(event) =>
                    setBulkCategoryIds((current) =>
                      event.target.checked
                        ? [...current, item.id]
                        : current.filter((id) => id !== item.id),
                    )
                  }
                />
                {item.name}
              </label>
            ))}
          </div>
          <Button
            disabled={selectedProductIds.length === 0}
            busy={bulkBusy}
            onClick={() => void moveSelectedProducts()}
          >
            Terapkan Kategori Terpilih
          </Button>
        </section>
      )}
      {loading && <Skeleton rows={8} cards />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && visible.length === 0 && (
        <EmptyState title="Produk tidak ditemukan" />
      )}
      {!loading && !error && visible.length > 0 && (
        <div
          className={`catalog-operation-grid catalog-operation-grid--${gridSize}${ownerProductBusy ? " catalog-operation-grid--loading" : ""}`}
          aria-busy={role === "OWNER" ? ownerProductBusy : undefined}
        >
          {visible.map((row) => {
            const key = keyFor(row);
            const out =
              (!row.branch.unlimited && row.branch.stock === 0) ||
              !row.branch.available;
            return (
              <article
                className={`catalog-operation-card ${out ? "catalog-operation-card--out" : ""}`}
                key={key}
              >
                <div className="catalog-operation-card__image">
                  <MediaImage mediaId={row.branch.mediaId} alt={row.name} />
                  <button
                    type="button"
                    aria-label={`Ubah foto ${row.name}`}
                    onClick={() => {
                      setPhotoTarget(row);
                      setSelectedMediaId(
                        row.branch.branchMediaId ?? row.branch.mediaId ?? "",
                      );
                      setMediaQuery("");
                      setSharePhotoAcrossBranches(false);
                    }}
                  >
                    <Camera />
                    <span>Ubah foto</span>
                  </button>
                </div>
                <div className="catalog-operation-card__body">
                  {role === "ADMIN" && (
                    <label className="product-select-check">
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(row.id)}
                        onChange={(event) =>
                          setSelectedProductIds((current) =>
                            event.target.checked
                              ? [...new Set([...current, row.id])]
                              : current.filter((id) => id !== row.id),
                          )
                        }
                      />
                      Pilih untuk pindahkan bersama
                    </label>
                  )}
                  <div className="catalog-operation-card__meta">
                    <span>{row.categoryNames.join(" · ")}</span>
                    <span className="catalog-operation-card__branch">
                      Cabang: {row.branch.branchName}
                    </span>
                  </div>
                  <h2>{row.name}</h2>
                  <p>
                    {row.branch.promoPrice !== null && (
                      <del>{formatRupiah(row.branch.price)} </del>
                    )}
                    <strong>
                      {formatRupiah(row.branch.promoPrice ?? row.branch.price)}
                    </strong>{" "}
                    / {row.branch.unit}
                  </p>
                  <Button
                    className="product-edit-button"
                    variant="secondary"
                    onClick={() => openEditProduct(row)}
                    icon={<Pencil />}
                  >
                    Ubah Nama/Harga/Satuan/Cabang
                  </Button>
                  <Button
                    className="product-delete-button"
                    variant="quiet"
                    onClick={() => setDeleteProductTarget(row)}
                    icon={<Trash2 />}
                  >
                    Hapus Produk
                  </Button>
                  <div className="stock-display">
                    <span>Stok khusus {row.branch.branchName}</span>
                    <strong>
                      {row.branch.unlimited
                        ? "Unlimited"
                        : row.branch.stock.toLocaleString("id-ID")}
                    </strong>
                    <small>
                      {row.branch.unlimited
                        ? "Tidak berkurang saat dibeli"
                        : row.branch.stock <= row.branch.minimumStock
                        ? "Stok rendah"
                        : "Stok aman"}
                    </small>
                  </div>
                  <div className="quick-stock">
                    <Button
                      variant="secondary"
                      disabled={
                        busyId === key ||
                        row.branch.unlimited ||
                        row.branch.stock === 0
                      }
                      onClick={() => void adjust(row, "SUBTRACT", 1)}
                      icon={<Minus />}
                    >
                      1
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={busyId === key || row.branch.unlimited}
                      onClick={() => void adjust(row, "ADD", 1)}
                      icon={<Plus />}
                    >
                      1
                    </Button>
                    <Button
                      variant="danger"
                      disabled={busyId === key || out || row.branch.unlimited}
                      onClick={() =>
                        void (async () => {
                          const stockUpdated = await adjust(row, "SET", 0);
                          if (stockUpdated) await updateAvailability(row, false);
                        })()
                      }
                      icon={<PackageX />}
                    >
                      Habis
                    </Button>
                  </div>
                  <div className="set-stock">
                    <input
                      inputMode="numeric"
                      min="0"
                      type="number"
                      placeholder="Total stok"
                      disabled={row.branch.unlimited}
                      value={setValues[key] ?? ""}
                      onChange={(event) =>
                        setSetValues((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                    />
                    <Button
                      disabled={
                        busyId === key ||
                        row.branch.unlimited ||
                        !setValues[key]
                      }
                      onClick={() =>
                        void adjust(
                          row,
                          "SET",
                          Math.max(0, Number(setValues[key])),
                        )
                      }
                    >
                      Set Total
                    </Button>
                    <label className="unlimited-toggle">
                      <input
                        type="checkbox"
                        checked={row.branch.unlimited}
                        disabled={busyId === key}
                        onChange={(event) =>
                          void updateUnlimited(row, event.target.checked)
                        }
                      />
                      <span>Unlimited</span>
                    </label>
                  </div>
                  {out && (
                    <Button
                      className="button--full"
                      disabled={busyId === key}
                      onClick={() => void updateAvailability(row, true)}
                      icon={<CheckCircle2 />}
                    >
                      Aktifkan Kembali
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
      {role === "OWNER" && !loading && !error && ownerProductPagination.total > 0 && (
        <nav
          className="catalog-pagination"
          aria-label="Halaman produk Owner"
          aria-busy={ownerProductBusy}
        >
          <button
            type="button"
            disabled={ownerProductBusy || ownerProductPagination.page <= 1}
            onClick={() =>
              setOwnerProductPage(Math.max(1, ownerProductPagination.page - 1))
            }
          >
            Sebelumnya
          </button>
          <span className="catalog-pagination__summary" aria-live="polite">
            <strong>
              Halaman {ownerProductPagination.page} dari{" "}
              {ownerProductPagination.totalPages}
            </strong>
            <small>
              {ownerProductBusy
                ? "Memuat daftar…"
                : `${ownerProductPagination.total.toLocaleString("id-ID")} produk`}
            </small>
          </span>
          <button
            type="button"
            disabled={ownerProductBusy || !ownerProductPagination.hasMore}
            onClick={() =>
              setOwnerProductPage(
                Math.min(
                  ownerProductPagination.totalPages,
                  ownerProductPagination.page + 1,
                ),
              )
            }
          >
            Berikutnya
          </button>
        </nav>
      )}

      <Modal
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(undefined)}
        title={`Ubah ${editTarget?.name ?? "Produk"}`}
        description={
          role === "OWNER"
            ? "Pilih cabang tujuan. Nama dan deskripsi diterapkan ke cabang yang dipilih; stok setiap cabang tetap terpisah."
            : `Nama, deskripsi, kategori, harga, dan satuan hanya berubah di ${editTarget?.branch.branchName ?? "cabang ini"}.`
        }
      >
        {editTarget && (
          <form
            className="modal-form"
            onSubmit={(event) => void saveProduct(event)}
          >
            <>
                <label>
                  Nama produk
                  <input
                    required
                    minLength={2}
                    maxLength={140}
                    value={editForm.name}
                    onChange={(event) =>
                      setEditForm({ ...editForm, name: event.target.value })
                    }
                  />
                </label>
                <label>
                  Deskripsi produk (opsional)
                  <textarea
                    rows={3}
                    maxLength={1000}
                    placeholder="Contoh: Cocok untuk jus, salad, atau dimakan langsung."
                    value={editForm.description}
                    onChange={(event) =>
                      setEditForm({
                        ...editForm,
                        description: event.target.value,
                      })
                    }
                  />
                  <span>
                    Kosongkan jika deskripsi tidak perlu ditampilkan. Deskripsi
                    berlaku pada cabang yang dipilih.
                  </span>
                </label>
                <section className="category-checkbox-section">
                  <h3>Tampilkan produk di kategori</h3>
                  <p>
                    Boleh pilih beberapa. Jika semuanya kosong, produk kembali
                    ke kategori awal.
                  </p>
                  <div className="category-check-grid">
                    {categoryOptions.map((item) => (
                      <label key={item.id}>
                        <input
                          type="checkbox"
                          checked={editForm.categoryIds.includes(item.id)}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              categoryIds: event.target.checked
                                ? [...editForm.categoryIds, item.id]
                                : editForm.categoryIds.filter(
                                    (id) => id !== item.id,
                                  ),
                            })
                          }
                        />
                        {item.name}
                      </label>
                    ))}
                  </div>
                </section>
            </>
            {role === "OWNER" && (
              <section className="category-checkbox-section">
                <h3>Produk tersedia di cabang</h3>
                <p>
                  Cabang yang tidak dicentang akan menyembunyikan produk ini.
                  Stok cabang tidak pernah disalin atau digabungkan.
                </p>
                <div className="category-check-grid">
                  {branchOptions.map((branch) => {
                    const selected = editForm.branchIds.includes(branch.id);
                    return (
                      <label
                        className={
                          selected ? "branch-check-option--selected" : undefined
                        }
                        key={branch.id}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;
                            setEditForm((current) => ({
                              ...current,
                              branchIds: updateSelectedBranchIds(
                                current.branchIds,
                                branch.id,
                                checked,
                              ),
                              branchPrices: checked
                                ? {
                                    ...current.branchPrices,
                                    [branch.id]: current.branchPrices[
                                      branch.id
                                    ] ?? {
                                      price: current.price,
                                      promoPrice: current.promoPrice,
                                    },
                                  }
                                : current.branchPrices,
                              branchStocks: checked
                                ? {
                                    ...current.branchStocks,
                                    [branch.id]: current.branchStocks[
                                      branch.id
                                    ] ?? {
                                      stock: "",
                                      initialStock: null,
                                      unlimited: false,
                                    },
                                  }
                                : current.branchStocks,
                            }));
                          }}
                        />
                        <span>{branch.name}</span>
                        {selected && <strong>Dipilih</strong>}
                      </label>
                    );
                  })}
                </div>
                <small className="branch-selection-summary" aria-live="polite">
                  {editForm.branchIds.length} cabang dipilih. Cabang baru akan
                  dibuatkan stok awal 0 dan dapat dikelola Admin cabang tersebut.
                </small>
                <label className="toggle-setting">
                  <input
                    type="checkbox"
                    checked={editForm.samePrice}
                    onChange={(event) =>
                      setEditForm((current) => ({
                        ...current,
                        samePrice: event.target.checked,
                      }))
                    }
                  />
                  <span>
                    <strong>Samakan harga dan promo di semua cabang</strong>
                    <small>
                      Hilangkan centang untuk mengisi harga berbeda per cabang.
                    </small>
                  </span>
                </label>
                <div className="branch-stock-editor">
                  <div>
                    <h3>Stok per cabang</h3>
                    <p>
                      Isi total stok atau pilih Unlimited. Perubahan stok dicatat
                      terpisah untuk setiap cabang.
                    </p>
                  </div>
                  {editForm.branchIds.map((branchId) => {
                    const branch = branchOptions.find(
                      (item) => item.id === branchId,
                    );
                    const branchStock = editForm.branchStocks[branchId] ?? {
                      stock: "",
                      initialStock: null,
                      unlimited: false,
                    };
                    return (
                      <div className="branch-stock-row" key={branchId}>
                        <strong>{branch?.name ?? "Cabang"}</strong>
                        <label>
                          Total stok
                          <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            max="1000000"
                            step="1"
                            disabled={branchStock.unlimited}
                            placeholder={
                              branchStock.initialStock === null
                                ? "Stok awal 0"
                                : undefined
                            }
                            value={branchStock.stock}
                            onChange={(event) => {
                              const stock = event.currentTarget.value;
                              setEditForm((current) => ({
                                ...current,
                                branchStocks: {
                                  ...current.branchStocks,
                                  [branchId]: {
                                    ...(current.branchStocks[branchId] ??
                                      branchStock),
                                    stock,
                                  },
                                },
                              }));
                            }}
                          />
                        </label>
                        <label className="branch-stock-unlimited">
                          <input
                            type="checkbox"
                            checked={branchStock.unlimited}
                            onChange={(event) => {
                              const unlimited = event.currentTarget.checked;
                              setEditForm((current) => ({
                                ...current,
                                branchStocks: {
                                  ...current.branchStocks,
                                  [branchId]: {
                                    ...(current.branchStocks[branchId] ??
                                      branchStock),
                                    unlimited,
                                  },
                                },
                              }));
                            }}
                          />
                          <span>Stok Unlimited</span>
                        </label>
                      </div>
                    );
                  })}
                  <small>
                    Cabang baru yang dikosongkan mulai dari stok 0. Cabang yang
                    pernah memakai produk ini mempertahankan stok terakhir bila
                    kolom stok tidak diubah.
                  </small>
                </div>
              </section>
            )}
            {(role !== "OWNER" || editForm.samePrice) && (
              <>
            <label>
              Harga normal (Rp)
              <input
                required
                type="number"
                min="0"
                step="1"
                value={editForm.price}
                onChange={(event) =>
                  setEditForm({ ...editForm, price: event.target.value })
                }
              />
            </label>
            <label>
              Harga promo (opsional)
              <input
                type="number"
                min="0"
                step="1"
                placeholder="Kosongkan jika tidak ada promo"
                value={editForm.promoPrice}
                onChange={(event) =>
                  setEditForm({ ...editForm, promoPrice: event.target.value })
                }
              />
              <span>
                Produk otomatis masuk bagian Promo saat harga promo aktif.
              </span>
            </label>
              </>
            )}
            {role === "OWNER" && !editForm.samePrice && (
              <section className="branch-price-editor">
                <h3>Harga per cabang</h3>
                {editForm.branchIds.map((branchId) => {
                  const branch = branchOptions.find(
                    (item) => item.id === branchId,
                  );
                  const branchPrice = editForm.branchPrices[branchId] ?? {
                    price: editForm.price,
                    promoPrice: editForm.promoPrice,
                  };
                  return (
                    <div className="branch-price-row" key={branchId}>
                      <strong>{branch?.name ?? "Cabang"}</strong>
                      <label>
                        Harga normal
                        <input
                          required
                          type="number"
                          min="0"
                          step="1"
                          value={branchPrice.price}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              branchPrices: {
                                ...editForm.branchPrices,
                                [branchId]: {
                                  ...branchPrice,
                                  price: event.target.value,
                                },
                              },
                            })
                          }
                        />
                      </label>
                      <label>
                        Promo (opsional)
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={branchPrice.promoPrice}
                          onChange={(event) =>
                            setEditForm({
                              ...editForm,
                              branchPrices: {
                                ...editForm.branchPrices,
                                [branchId]: {
                                  ...branchPrice,
                                  promoPrice: event.target.value,
                                },
                              },
                            })
                          }
                        />
                      </label>
                    </div>
                  );
                })}
              </section>
            )}
            <label>
              Satuan jual
              <input
                required
                maxLength={40}
                placeholder="kg, ikat, 500 g, pouch…"
                value={editForm.unit}
                onChange={(event) =>
                  setEditForm({ ...editForm, unit: event.target.value })
                }
              />
            </label>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditTarget(undefined)}
              >
                Batal
              </Button>
              <Button
                type="submit"
                busy={busyId === keyFor(editTarget)}
                icon={<Save />}
              >
                Simpan
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={Boolean(photoTarget)}
        onClose={() => {
          setPhotoTarget(undefined);
          setOptimized(undefined);
          setMediaQuery("");
          setSharePhotoAcrossBranches(false);
        }}
        title={`Foto ${photoTarget?.name ?? "Produk"}`}
        description="Klik foto untuk memilih, lalu tekan Gunakan Foto Ini."
      >
        {photoTarget && (
          <div className="photo-picker">
            <section>
              <h3>Gunakan gambar yang sudah ada</h3>
              <label className="media-search">
                <Search aria-hidden="true" />
                <span className="sr-only">Cari nama foto</span>
                <input
                  value={mediaQuery}
                  onChange={(event) => setMediaQuery(event.target.value)}
                  placeholder="Cari nama foto atau produk…"
                />
              </label>
              <div className="media-choice-grid">
                <button
                  type="button"
                  className={
                    !selectedMediaId
                      ? "media-choice media-choice--selected"
                      : "media-choice"
                  }
                  onClick={() => setSelectedMediaId("")}
                >
                  <span className="media-placeholder">
                    <ImagePlus />
                  </span>
                  <strong>Gunakan foto default</strong>
                </button>
                {filteredMedia.map((item) => (
                  <button
                    type="button"
                    className={
                      selectedMediaId === item.id
                        ? "media-choice media-choice--selected"
                        : "media-choice"
                    }
                    onClick={() => setSelectedMediaId(item.id)}
                    key={item.id}
                  >
                    <img
                      src={`/api/media/private/${encodeURIComponent(item.id)}`}
                      alt={item.filename}
                    />
                    <strong>{item.filename}</strong>
                  </button>
                ))}
              </div>
              {mediaPagination.hasMore && (
                <Button
                  type="button"
                  variant="secondary"
                  className="button--full"
                  busy={mediaBusy}
                  onClick={() => void loadMoreMedia()}
                >
                  Muat 50 Foto Berikutnya
                </Button>
              )}
              {filteredMedia.length === 0 && (
                <Notice tone="info">Nama foto tidak ditemukan.</Notice>
              )}
              {role === "OWNER" && (
                <label className="shared-media-option">
                  <input
                    type="checkbox"
                    checked={sharePhotoAcrossBranches}
                    onChange={(event) =>
                      setSharePhotoAcrossBranches(event.target.checked)
                    }
                  />
                  <span>
                    <strong>Gunakan sebagai foto default semua cabang</strong>
                    <small>
                      File yang sama dipakai tanpa duplikasi. Foto khusus cabang
                      tetap diprioritaskan dan Admin dapat menggantinya sendiri.
                    </small>
                  </span>
                </label>
              )}
              <Button
                className="button--full"
                busy={busyId === keyFor(photoTarget)}
                disabled={sharePhotoAcrossBranches && !selectedMediaId}
                onClick={() => void attachMedia(selectedMediaId)}
                icon={<ImagePlus />}
              >
                Gunakan Foto Ini
              </Button>
            </section>
            <div className="photo-picker__or">atau</div>
            <section>
              <h3>Unggah foto baru</h3>
              <label className="compact-upload">
                <Upload />
                <span>Pilih JPG, PNG, atau WebP</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(event) => void chooseFile(event)}
                />
              </label>
              {optimized && (
                <div className="optimized-inline">
                  <img
                    src={URL.createObjectURL(optimized.blob)}
                    alt="Pratinjau foto"
                  />
                  <p>{Math.round(optimized.optimizedBytes / 1024)} KB · WebP</p>
                  <Button
                    className="button--full"
                    onClick={() => void uploadAndAttach()}
                  >
                    Unggah & Pakai
                  </Button>
                </div>
              )}
            </section>
          </div>
        )}
      </Modal>

      <Modal
        open={categoryImageModal}
        onClose={() => {
          setCategoryImageModal(false);
          setCategoryImageOptimized(undefined);
          setMediaQuery("");
        }}
        title="Gambar Kategori Cabang"
        description="Pilih foto khusus atau biarkan otomatis memakai foto produk pertama dalam kategori."
      >
        <div className="modal-form">
          <label>
            Kategori
            <select
              value={categoryImageCategoryId}
              onChange={(event) => {
                const categoryId = event.target.value;
                const selected = categoryOptions.find(
                  (item) => item.id === categoryId,
                );
                setCategoryImageCategoryId(categoryId);
                setCategoryImageMediaId(selected?.selectedMediaId ?? "");
                setCategoryImageOptimized(undefined);
              }}
            >
              {categoryOptions.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <Notice tone="info">
            Pilihan otomatis akan mengambil gambar produk pertama yang tersedia
            di kategori dan cabang ini.
          </Notice>
          <section className="category-image-upload">
            <h3>Upload gambar baru</h3>
            <label className="compact-upload">
              <Upload aria-hidden="true" />
              <span>Pilih JPG, PNG, atau WebP</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void chooseCategoryImageFile(event)}
              />
            </label>
            {categoryImageOptimized && (
              <div className="optimized-inline">
                <img
                  src={URL.createObjectURL(categoryImageOptimized.blob)}
                  alt="Pratinjau gambar kategori baru"
                />
                <p>
                  {Math.round(categoryImageOptimized.optimizedBytes / 1024)} KB · WebP
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  busy={bulkBusy}
                  onClick={() => void uploadCategoryImage()}
                  icon={<Upload aria-hidden="true" />}
                >
                  Upload & Pilih Gambar Ini
                </Button>
              </div>
            )}
          </section>
          <label className="media-search">
            <Search aria-hidden="true" />
            <span className="sr-only">Cari nama gambar kategori</span>
            <input
              value={mediaQuery}
              onChange={(event) => setMediaQuery(event.target.value)}
              placeholder="Cari nama foto…"
            />
          </label>
          <div className="media-choice-grid">
            <button
              type="button"
              className={
                !categoryImageMediaId
                  ? "media-choice media-choice--selected"
                  : "media-choice"
              }
              onClick={() => setCategoryImageMediaId("")}
            >
              <MediaImage
                mediaId={
                  categoryOptions.find(
                    (item) => item.id === categoryImageCategoryId,
                  )?.mediaId
                }
                alt="Pratinjau gambar otomatis"
              />
              <strong>Otomatis dari produk</strong>
            </button>
            {filteredMedia.map((item) => (
              <button
                type="button"
                className={
                  categoryImageMediaId === item.id
                    ? "media-choice media-choice--selected"
                    : "media-choice"
                }
                onClick={() => setCategoryImageMediaId(item.id)}
                key={item.id}
              >
                <img
                  src={`/api/media/private/${encodeURIComponent(item.id)}`}
                  alt={item.filename}
                />
                <strong>{item.filename}</strong>
              </button>
            ))}
          </div>
          {mediaPagination.hasMore && (
            <Button
              type="button"
              variant="secondary"
              busy={mediaBusy}
              onClick={() => void loadMoreMedia()}
            >
              Muat 50 Foto Berikutnya
            </Button>
          )}
          <div className="modal-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCategoryImageModal(false)}
            >
              Batal
            </Button>
            <Button
              type="button"
              busy={bulkBusy}
              onClick={() => void saveCategoryImage()}
              icon={<Save />}
            >
              Simpan Gambar Kategori
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={productModal}
        onClose={() => {
          setProductModal(false);
          setNewProductOptimized(undefined);
          setMediaQuery("");
        }}
        title="Tambah Produk"
        description={
          role === "OWNER"
            ? "Pilih kategori dan satu atau beberapa cabang tempat produk dijual."
            : `Produk baru akan dijual hanya di ${user?.branchName ?? "cabang Admin"}. Owner tetap dapat melihatnya.`
        }
      >
        <form
          className="modal-form"
          onSubmit={(event) => void createProduct(event)}
        >
          <section className="category-checkbox-section">
            <h3>1. Tampilkan di kategori</h3>
            <p>Pilih satu atau beberapa kategori produk.</p>
            <div className="category-check-grid">
              {categoryOptions.map((item) => (
                <label key={item.id}>
                  <input
                    type="checkbox"
                    checked={newProduct.categoryIds.includes(item.id)}
                    onChange={(event) =>
                      setNewProduct({
                        ...newProduct,
                        categoryIds: event.target.checked
                          ? [...newProduct.categoryIds, item.id]
                          : newProduct.categoryIds.filter(
                              (id) => id !== item.id,
                            ),
                      })
                    }
                  />
                  {item.name}
                </label>
              ))}
            </div>
            <span>Belum ada? Tutup form ini lalu tekan Tambah Kategori.</span>
          </section>
          {role === "OWNER" && (
            <section className="category-checkbox-section">
              <h3>2. Dijual di cabang</h3>
              <p>Harga dan stok awal di bawah diterapkan pada cabang terpilih.</p>
              <div className="category-check-grid">
                {branchOptions.map((branch) => {
                  const selected = newProduct.branchIds.includes(branch.id);
                  return (
                    <label
                      className={
                        selected ? "branch-check-option--selected" : undefined
                      }
                      key={branch.id}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          setNewProduct((current) => ({
                            ...current,
                            branchIds: updateSelectedBranchIds(
                              current.branchIds,
                              branch.id,
                              checked,
                            ),
                          }));
                        }}
                      />
                      <span>{branch.name}</span>
                      {selected && <strong>Dipilih</strong>}
                    </label>
                  );
                })}
              </div>
              <small className="branch-selection-summary" aria-live="polite">
                {newProduct.branchIds.length} cabang dipilih.
              </small>
            </section>
          )}
          <label>
            {role === "OWNER" ? "3" : "2"}. Nama produk
            <input
              required
              minLength={2}
              maxLength={140}
              placeholder="Contoh: Tomat Segar"
              value={newProduct.name}
              onChange={(event) =>
                setNewProduct({ ...newProduct, name: event.target.value })
              }
            />
          </label>
          <label>
            Deskripsi singkat (opsional)
            <textarea
              rows={2}
              maxLength={1000}
              placeholder="Keterangan singkat untuk pelanggan"
              value={newProduct.description}
              onChange={(event) =>
                setNewProduct({
                  ...newProduct,
                  description: event.target.value,
                })
              }
            />
          </label>
          <div className="two-field-grid">
            <label>
              {role === "OWNER" ? "4" : "3"}. Harga normal (Rp)
              <input
                required
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                placeholder="15000"
                value={newProduct.price}
                onChange={(event) =>
                  setNewProduct({ ...newProduct, price: event.target.value })
                }
              />
            </label>
            <label>
              Harga promo (opsional)
              <input
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                placeholder="Kosongkan jika tidak promo"
                value={newProduct.promoPrice}
                onChange={(event) =>
                  setNewProduct({
                    ...newProduct,
                    promoPrice: event.target.value,
                  })
                }
              />
            </label>
          </div>
          <div className="two-field-grid">
            <label>
              {role === "OWNER" ? "5" : "4"}. Jual per apa?
              <input
                required
                maxLength={40}
                placeholder="kg, ikat, 500 g, kotak…"
                value={newProduct.unit}
                onChange={(event) =>
                  setNewProduct({ ...newProduct, unit: event.target.value })
                }
              />
            </label>
            <label>
              {role === "OWNER" ? "6" : "5"}. Stok awal
              <input
                required={!newProduct.unlimited}
                disabled={newProduct.unlimited}
                type="number"
                inputMode="numeric"
                min="0"
                step="1"
                placeholder="0"
                value={newProduct.stock}
                onChange={(event) =>
                  setNewProduct({ ...newProduct, stock: event.target.value })
                }
              />
            </label>
          </div>
          <label className="toggle-setting">
            <input
              type="checkbox"
              checked={newProduct.unlimited}
              onChange={(event) =>
                setNewProduct({
                  ...newProduct,
                  unlimited: event.target.checked,
                  stock: event.target.checked ? "0" : newProduct.stock,
                })
              }
            />
            <span>
              <strong>Stok Unlimited</strong>
              <small>Jumlah tidak berkurang saat pelanggan membeli.</small>
            </span>
          </label>
          <section className="new-product-photo">
            <h3>{role === "OWNER" ? "7" : "6"}. Foto produk (opsional)</h3>
            <p>Pilih gambar yang sudah ada atau unggah foto baru.</p>
            <label className="media-search">
              <Search aria-hidden="true" />
              <span className="sr-only">Cari nama foto produk</span>
              <input
                value={mediaQuery}
                onChange={(event) => setMediaQuery(event.target.value)}
                placeholder="Cari nama foto atau produk…"
              />
            </label>
            <div className="media-choice-grid media-choice-grid--compact">
              <button
                type="button"
                className={
                  !newProductMediaId && !newProductOptimized
                    ? "media-choice media-choice--selected"
                    : "media-choice"
                }
                onClick={() => {
                  setNewProductMediaId("");
                  setNewProductOptimized(undefined);
                }}
              >
                <span className="media-placeholder">
                  <ImagePlus />
                </span>
                <strong>Tanpa foto</strong>
              </button>
              {filteredMedia.map((item) => (
                <button
                  type="button"
                  className={
                    newProductMediaId === item.id && !newProductOptimized
                      ? "media-choice media-choice--selected"
                      : "media-choice"
                  }
                  onClick={() => {
                    setNewProductMediaId(item.id);
                    setNewProductOptimized(undefined);
                  }}
                  key={item.id}
                >
                  <img
                    src={`/api/media/private/${encodeURIComponent(item.id)}`}
                    alt={item.filename}
                  />
                  <strong>{item.filename}</strong>
                </button>
              ))}
            </div>
            {mediaPagination.hasMore && (
              <Button
                type="button"
                variant="secondary"
                busy={mediaBusy}
                onClick={() => void loadMoreMedia()}
              >
                Muat 50 Foto Berikutnya
              </Button>
            )}
            <label className="compact-upload">
              <Upload />
              <span>Pilih foto baru</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void chooseNewProductFile(event)}
              />
            </label>
            {newProductOptimized && (
              <div className="optimized-inline">
                <img
                  src={URL.createObjectURL(newProductOptimized.blob)}
                  alt="Pratinjau foto produk baru"
                />
                <p>
                  Foto baru siap ·{" "}
                  {Math.round(newProductOptimized.optimizedBytes / 1024)} KB
                  WebP
                </p>
              </div>
            )}
          </section>
          <Notice tone="info">
            Jika harga promo diisi, produk otomatis masuk bagian Promo di main
            website cabang ini.
          </Notice>
          <div className="modal-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setProductModal(false)}
            >
              Batal
            </Button>
            <Button type="submit" busy={creatingProduct} icon={<Plus />}>
              Tambahkan Produk
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={categoryModal}
        onClose={() => {
          setCategoryModal(false);
          setCategoryEditId(undefined);
        }}
        title={categoryEditId ? "Ubah Kategori" : "Tambah Kategori"}
        description={
          categoryEditId
            ? "Nama baru langsung digunakan pada produk dan main website."
            : "Kategori aktif langsung muncul di navigasi dan main website."
        }
      >
        <form
          className="modal-form"
          onSubmit={(event) => void saveCategory(event)}
        >
          <label>
            Nama kategori
            <input
              required
              minLength={2}
              value={categoryForm.name}
              onChange={(event) => {
                const name = event.target.value;
                setCategoryForm({
                  ...categoryForm,
                  name,
                  slug: name
                    .toLowerCase()
                    .trim()
                    .replace(/[^a-z0-9]+/gu, "-")
                    .replace(/^-|-$/gu, ""),
                });
              }}
            />
          </label>
          <label>
            Slug URL
            <input
              required
              pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
              value={categoryForm.slug}
              onChange={(event) =>
                setCategoryForm({ ...categoryForm, slug: event.target.value })
              }
            />
          </label>
          <label>
            Deskripsi
            <textarea
              rows={3}
              value={categoryForm.description}
              onChange={(event) =>
                setCategoryForm({
                  ...categoryForm,
                  description: event.target.value,
                })
              }
            />
          </label>
          <div className="modal-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCategoryModal(false)}
            >
              Batal
            </Button>
            <Button type="submit">
              {categoryEditId ? "Simpan Perubahan" : "Tambah Kategori"}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(deleteProductTarget)}
        onClose={() => setDeleteProductTarget(undefined)}
        title={`Hapus ${deleteProductTarget?.name ?? "produk"}?`}
        description={`Produk akan disembunyikan hanya dari ${deleteProductTarget?.branch.branchName ?? "cabang ini"}. Stok tersimpan dan riwayat pesanan lama tidak ikut dihapus.`}
      >
        <div className="modal-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setDeleteProductTarget(undefined)}
          >
            Batal
          </Button>
          <Button
            type="button"
            variant="danger"
            busy={
              deleteProductTarget
                ? busyId === keyFor(deleteProductTarget)
                : false
            }
            onClick={() => void deleteProduct()}
            icon={<Trash2 />}
          >
            Hapus dari Cabang
          </Button>
        </div>
      </Modal>

      <Modal
        open={Boolean(categoryDeleteTarget)}
        onClose={() => setCategoryDeleteTarget(undefined)}
        title={`Hapus kategori ${categoryDeleteTarget?.name ?? ""}?`}
        description="Semua produk di kategori ini otomatis dipindahkan ke Other. Produk, stok, harga, dan riwayat pesanan tidak ikut dihapus."
      >
        <div className="modal-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setCategoryDeleteTarget(undefined)}
          >
            Batal
          </Button>
          <Button
            type="button"
            variant="danger"
            busy={bulkBusy}
            onClick={() => void deleteCategory()}
          >
            Hapus & Pindahkan ke Other
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export function CategoryManagementPage() {
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [categoryMedia, setCategoryMedia] = useState<MediaOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ApiRecord>();
  const [deleteTarget, setDeleteTarget] = useState<ApiRecord>();
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    mediaId: "",
  });
  const [categoryMediaQuery, setCategoryMediaQuery] = useState("");
  const [categoryImageOptimized, setCategoryImageOptimized] =
    useState<OptimizedImage>();
  const [categoryImagePreview, setCategoryImagePreview] = useState<string>();
  const [categoryImageNotice, setCategoryImageNotice] = useState<string>();

  const filteredCategoryMedia = useMemo(() => {
    const needle = categoryMediaQuery.trim().toLocaleLowerCase("id-ID");
    if (!needle) return categoryMedia;
    return categoryMedia.filter((item) =>
      item.filename.toLocaleLowerCase("id-ID").includes(needle),
    );
  }, [categoryMedia, categoryMediaQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [categoryPayload, mediaPayload] = await Promise.all([
        apiRequest<unknown>("/api/owner/categories"),
        apiRequest<unknown>("/api/owner/media"),
      ]);
      setItems(normalizeCollection(categoryPayload).items);
      setCategoryMedia(
        normalizeCollection(mediaPayload)
          .items.filter((item) => {
            const branch =
              "branchId" in item ? item.branchId : item.branch_id;
            const mediaType = text(value(item, "mediaType", "media_type"));
            return (
              branch === null &&
              text(value(item, "lifecycleStatus", "lifecycle_status")) ===
                "active" &&
              ["product", "category"].includes(mediaType)
            );
          })
          .map((item) => ({
            id: text(item.id),
            filename: text(value(item, "filename", "name"), "Foto kategori"),
          }))
          .filter((item) => item.id),
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Kategori belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  useEffect(
    () => () => {
      if (categoryImagePreview) URL.revokeObjectURL(categoryImagePreview);
    },
    [categoryImagePreview],
  );

  const openCreate = () => {
    setEditing({});
    setForm({ name: "", slug: "", description: "", mediaId: "" });
    setCategoryMediaQuery("");
    setCategoryImageOptimized(undefined);
    setCategoryImagePreview(undefined);
    setCategoryImageNotice(undefined);
  };
  const openEdit = (item: ApiRecord) => {
    setEditing(item);
    setForm({
      name: text(item.name),
      slug: text(item.slug),
      description: text(item.description),
      mediaId: text(value(item, "mediaId", "media_id")),
    });
    setCategoryMediaQuery("");
    setCategoryImageOptimized(undefined);
    setCategoryImagePreview(undefined);
    setCategoryImageNotice(undefined);
  };
  const closeCategoryEditor = () => {
    setEditing(undefined);
    setCategoryImageOptimized(undefined);
    setCategoryImagePreview(undefined);
    setCategoryImageNotice(undefined);
  };
  const chooseCategoryImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCategoryImageNotice(undefined);
    try {
      const optimized = await optimizeProductImage(file);
      setCategoryImageOptimized(optimized);
      setCategoryImagePreview(URL.createObjectURL(optimized.blob));
    } catch (caught) {
      setCategoryImageNotice(
        caught instanceof Error
          ? caught.message
          : "Gambar kategori belum dapat diproses.",
      );
    }
  };
  const uploadCategoryImage = async () => {
    if (!categoryImageOptimized) return;
    setBusy(true);
    setCategoryImageNotice(undefined);
    try {
      const uploaded = await apiRequest<{ id: string }>("/api/media", {
        method: "POST",
        ...jsonBody({
          filename: categoryImageOptimized.filename,
          originalFilename: categoryImageOptimized.filename,
          mediaType: "category",
          altText: `Gambar kategori ${form.name || "produk"}`,
          dataBase64: await blobToBase64(categoryImageOptimized.blob),
        }),
      });
      setCategoryMedia((current) => [
        { id: uploaded.id, filename: categoryImageOptimized.filename },
        ...current.filter((item) => item.id !== uploaded.id),
      ]);
      setForm((current) => ({ ...current, mediaId: uploaded.id }));
      setCategoryImageOptimized(undefined);
      setCategoryImagePreview(undefined);
      setCategoryImageNotice(
        "Foto berhasil diunggah dan dipilih. Tekan Simpan Kategori untuk memakainya.",
      );
    } catch (caught) {
      setCategoryImageNotice(
        caught instanceof Error
          ? caught.message
          : "Gambar kategori belum berhasil diunggah.",
      );
    } finally {
      setBusy(false);
    }
  };
  const saveCategory = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    const id = text(editing.id);
    setBusy(true);
    setNotice(undefined);
    try {
      await apiRequest(
        id ? `/api/owner/categories/${encodeURIComponent(id)}` : "/api/owner/categories",
        {
          method: id ? "PATCH" : "POST",
          ...jsonBody({
            name: form.name,
            slug: form.slug,
            description: form.description || null,
            mediaId: form.mediaId || null,
            ...(!id
              ? {
                  displayOrder: items.length + 1,
                  isActive: true,
                }
              : {}),
          }),
        },
      );
      closeCategoryEditor();
      setNotice(id ? "Kategori berhasil diperbarui." : "Kategori berhasil ditambahkan.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Kategori belum berhasil disimpan.",
      );
    } finally {
      setBusy(false);
    }
  };
  const archiveCategory = async () => {
    const id = text(deleteTarget?.id);
    if (!id) return;
    setBusy(true);
    setNotice(undefined);
    try {
      const result = await apiRequest<{ movedProducts?: number }>(
        `/api/owner/categories/${encodeURIComponent(id)}`,
        { method: "DELETE", ...jsonBody({}) },
      );
      setDeleteTarget(undefined);
      setNotice(
        `Kategori berhasil dihapus. ${result.movedProducts ?? 0} produk dipindahkan ke Other.`,
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Kategori belum berhasil dihapus.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Kelola katalog</span>
          <h1>Kategori Produk</h1>
          <p>Ubah nama dan gambar kategori atau hapus dengan pemindahan produk otomatis ke Other.</p>
        </div>
        <div className="heading-actions">
          <Button variant="secondary" onClick={() => void load()} icon={<RefreshCw />}>
            Perbarui
          </Button>
          <Button onClick={openCreate} icon={<Plus />}>Tambah Kategori</Button>
        </div>
      </div>
      <Notice tone="info">
        Kategori Other adalah tempat cadangan sistem dan tidak dapat dihapus.
      </Notice>
      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      {loading && <Skeleton rows={6} cards />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && items.length === 0 && (
        <EmptyState title="Belum ada kategori" />
      )}
      {!loading && !error && items.length > 0 && (
        <div className="category-management-grid">
          {items.map((item) => {
            const protectedCategory = text(item.slug) === "other";
            return (
              <article className="management-card" key={text(item.id)}>
                <div className="management-card__category-media">
                  <MediaImage
                    mediaId={
                      text(value(item, "mediaId", "media_id")) || undefined
                    }
                    alt={`Kategori ${text(item.name)}`}
                  />
                  <small>
                    {text(value(item, "mediaId", "media_id"))
                      ? "Gambar manual"
                      : "Otomatis dari foto produk pertama"}
                  </small>
                </div>
                <div className="management-card__top">
                  <div>
                    <small>/{text(item.slug)}</small>
                    <strong>{text(item.name)}</strong>
                  </div>
                  <StatusBadge status={bool(value(item, "isActive", "is_active"), true) ? "active" : "inactive"} />
                </div>
                <p>{text(item.description, "Tanpa deskripsi")}</p>
                <div className="management-card__actions">
                  <Button
                    variant="secondary"
                    onClick={() => openEdit(item)}
                    icon={<Pencil />}
                  >
                    Ubah Nama/Gambar Kategori
                  </Button>
                  <Button
                    variant="danger"
                    disabled={protectedCategory}
                    onClick={() => setDeleteTarget(item)}
                    icon={<Trash2 />}
                  >
                    Hapus
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
      <Modal
        open={Boolean(editing)}
        onClose={closeCategoryEditor}
        title={text(editing?.id) ? "Ubah Nama/Gambar Kategori" : "Tambah Kategori"}
        description="Pilih gambar manual atau biarkan otomatis memakai foto produk pertama dalam kategori."
      >
        <form className="modal-form" onSubmit={(event) => void saveCategory(event)}>
          <label>
            Nama kategori
            <input
              required
              minLength={2}
              maxLength={100}
              disabled={text(editing?.slug) === "other"}
              value={form.name}
              onChange={(event) => {
                const name = event.target.value;
                setForm({
                  ...form,
                  name,
                  slug: text(editing?.id)
                    ? form.slug
                    : name.toLowerCase().trim().replace(/[^a-z0-9]+/gu, "-").replace(/^-|-$/gu, ""),
                });
              }}
            />
          </label>
          <label>
            Slug URL
            <input
              required
              pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
              disabled={text(editing?.slug) === "other"}
              value={form.slug}
              onChange={(event) => setForm({ ...form, slug: event.target.value })}
            />
          </label>
          <label>
            Deskripsi
            <textarea
              rows={3}
              maxLength={500}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </label>
          <section className="category-image-upload">
            <h3>Gambar kategori (opsional)</h3>
            <Notice tone="info">
              Jika memilih Otomatis, main website mengambil foto produk pertama
              yang tersedia pada kategori di masing-masing cabang.
            </Notice>
            <label className="media-search">
              <Search aria-hidden="true" />
              <span className="sr-only">Cari nama gambar kategori</span>
              <input
                value={categoryMediaQuery}
                onChange={(event) => setCategoryMediaQuery(event.target.value)}
                placeholder="Cari nama foto…"
              />
            </label>
            <div className="media-choice-grid">
              <button
                type="button"
                className={
                  !form.mediaId
                    ? "media-choice media-choice--selected"
                    : "media-choice"
                }
                aria-pressed={!form.mediaId}
                onClick={() => setForm({ ...form, mediaId: "" })}
              >
                <span className="media-placeholder">
                  <ImagePlus aria-hidden="true" />
                </span>
                <strong>Otomatis dari produk</strong>
              </button>
              {filteredCategoryMedia.map((item) => (
                <button
                  type="button"
                  className={
                    form.mediaId === item.id
                      ? "media-choice media-choice--selected"
                      : "media-choice"
                  }
                  aria-pressed={form.mediaId === item.id}
                  onClick={() => setForm({ ...form, mediaId: item.id })}
                  key={item.id}
                >
                  <img
                    src={`/api/media/private/${encodeURIComponent(item.id)}`}
                    alt={item.filename}
                  />
                  <strong>{item.filename}</strong>
                </button>
              ))}
            </div>
            {filteredCategoryMedia.length === 0 && categoryMediaQuery && (
              <Notice tone="info">Nama foto tidak ditemukan.</Notice>
            )}
            <label className="compact-upload">
              <Upload aria-hidden="true" />
              <span>Pilih JPG, PNG, atau WebP baru</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void chooseCategoryImage(event)}
              />
            </label>
            {categoryImagePreview && categoryImageOptimized && (
              <div className="optimized-inline">
                <img src={categoryImagePreview} alt="Pratinjau gambar kategori" />
                <p>
                  {Math.round(categoryImageOptimized.optimizedBytes / 1024)} KB · WebP
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  busy={busy}
                  onClick={() => void uploadCategoryImage()}
                  icon={<Upload aria-hidden="true" />}
                >
                  Upload & Pilih Gambar Ini
                </Button>
              </div>
            )}
            {categoryImageNotice && (
              <Notice
                tone={
                  categoryImageNotice.includes("berhasil") ? "success" : "danger"
                }
              >
                {categoryImageNotice}
              </Notice>
            )}
          </section>
          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={closeCategoryEditor}>
              Batal
            </Button>
            <Button type="submit" busy={busy}>Simpan Kategori</Button>
          </div>
        </form>
      </Modal>
      <Modal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(undefined)}
        title={`Hapus kategori ${text(deleteTarget?.name)}?`}
        description="Produk di dalamnya otomatis dipindahkan ke Other. Stok, harga, dan pesanan tetap aman."
      >
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setDeleteTarget(undefined)}>Batal</Button>
          <Button variant="danger" busy={busy} onClick={() => void archiveCategory()}>
            Hapus & Pindahkan Produk
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function orderNext(status: string, deliveryType: string): string | undefined {
  if (status === "NEW" || status === "WAITING_CONFIRMATION") return "CONFIRMED";
  if (status === "CONFIRMED" || status === "PAID") return "PREPARING";
  if (status === "PREPARING")
    return deliveryType === "PICKUP"
      ? "READY_FOR_PICKUP"
      : "READY_FOR_DELIVERY";
  if (status === "READY_FOR_DELIVERY") return "DELIVERING";
  if (status === "READY_FOR_PICKUP") return "COMPLETED";
  return undefined;
}

export function OrderManagementPage({
  role,
  paymentsOnly = false,
}: {
  role: SessionUser["role"];
  paymentsOnly?: boolean;
}) {
  const endpoint = role === "OWNER" ? "/api/owner/orders" : "/api/admin/orders";
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(
    paymentsOnly ? "WAITING_VERIFICATION" : "",
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [archiveTarget, setArchiveTarget] = useState<ApiRecord>();
  const [realTimeConnected, setRealTimeConnected] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (status) params.set("status", status);
      params.set("limit", "50");
      setItems(
        normalizeCollection(
          await apiRequest<unknown>(`${endpoint}?${params.toString()}`),
        ).items,
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Pesanan belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [endpoint, query, status]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);
  useEffect(() => {
    let stopped = false;
    let socket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    const connect = () => {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(`${protocol}//${window.location.host}/api/events/orders`);
      socket.addEventListener("open", () => setRealTimeConnected(true));
      socket.addEventListener("message", (event) => {
        if (event.data === "pong") return;
        try {
          const payload = JSON.parse(String(event.data)) as { type?: string; orderNumber?: string };
          if (payload.type === "ORDER_CREATED") {
            setNotice(`Pesanan baru ${payload.orderNumber ?? ""} masuk. Daftar telah diperbarui.`);
            void load();
          }
        } catch {
          // Pesan asing diabaikan; data pesanan selalu dimuat ulang dari API.
        }
      });
      socket.addEventListener("close", () => {
        setRealTimeConnected(false);
        if (!stopped) reconnectTimer = window.setTimeout(connect, 5000);
      });
      socket.addEventListener("error", () => socket?.close());
    };
    connect();
    return () => {
      stopped = true;
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      socket?.close(1000, "Halaman ditutup");
    };
  }, [load]);
  const update = async (
    item: ApiRecord,
    nextStatus: string,
    payment = false,
  ) => {
    const id = text(item.id);
    setBusyId(id);
    try {
      await apiRequest(
        `${endpoint}/${encodeURIComponent(id)}/${payment ? "payment" : "status"}`,
        {
          method: "PATCH",
          ...jsonBody(
            payment
              ? { status: nextStatus }
              : {
                  status: nextStatus,
                  ...(["CANCELLED", "REJECTED"].includes(nextStatus)
                    ? { reason: "Diperbarui melalui dashboard" }
                    : {}),
                },
          ),
        },
      );
      setNotice("Status pesanan berhasil diperbarui.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Status belum berhasil diperbarui.",
      );
    } finally {
      setBusyId(undefined);
    }
  };
  const archive = async () => {
    if (!archiveTarget) return;
    const id = text(archiveTarget.id);
    setBusyId(id);
    try {
      await apiRequest(`/api/admin/orders/${encodeURIComponent(id)}`, {
        method: "DELETE",
        ...jsonBody({ reason: "Diarsipkan manual oleh Admin" }),
      });
      setArchiveTarget(undefined);
      setNotice(
        "Pesanan dihapus dari Admin dan tetap terlihat Owner selama 30 hari.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Pesanan belum dapat diarsipkan.",
      );
    } finally {
      setBusyId(undefined);
    }
  };
  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Operasional cabang</span>
          <h1>{paymentsOnly ? "Pembayaran" : "Pesanan"}</h1>
          <p>
            Cabang, pelanggan, pengiriman, notifikasi, dan arsip tersusun dalam
            kartu yang mudah dibaca.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void load()}
          icon={<RefreshCw />}
        >
          Perbarui
        </Button>
      </div>
      {!paymentsOnly && (
        <Notice tone="info">
          Reply pesan WhatsApp terkait dengan <strong>SELESAI</strong> setelah
          barang benar-benar diserahkan. Admin hanya dapat menghapus pesanan
          yang sudah terminal; Owner menyimpan tampilan arsip 30 hari.
        </Notice>
      )}
      {!paymentsOnly && (
        <div className={`realtime-status ${realTimeConnected ? "realtime-status--connected" : ""}`}>
          <span aria-hidden="true" />
          {realTimeConnected
            ? "Live — pesanan baru muncul otomatis"
            : "Menghubungkan notifikasi pesanan…"}
        </div>
      )}
      {notice && (
        <Notice
          tone={
            notice.includes("berhasil") || notice.includes("30 hari")
              ? "success"
              : "danger"
          }
        >
          {notice}
        </Notice>
      )}
      <div className="toolbar">
        <label className="dashboard-search">
          <Search />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nomor pesanan atau pelanggan…"
          />
        </label>
        <label className="toolbar-filter">
          <Filter />
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">Semua status</option>
            <option value="NEW">Pesanan baru</option>
            <option value="CONFIRMED">Dikonfirmasi</option>
            <option value="PREPARING">Disiapkan</option>
            <option value="READY_FOR_DELIVERY">Siap dikirim</option>
            <option value="DELIVERING">Sedang dikirim</option>
            <option value="READY_FOR_PICKUP">Siap diambil</option>
            <option value="WAITING_VERIFICATION">Verifikasi pembayaran</option>
            <option value="COMPLETED">Selesai</option>
            <option value="CANCELLED">Dibatalkan</option>
          </select>
        </label>
      </div>
      {loading && <Skeleton rows={6} />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && items.length === 0 && (
        <EmptyState title="Belum ada pesanan" />
      )}
      {!loading && !error && (
        <div className="order-management-list">
          {items.map((item) => {
            const id = text(item.id);
            const orderStatus = text(
              value(item, "orderStatus", "order_status"),
            );
            const paymentStatus = text(
              value(item, "paymentStatus", "payment_status"),
            );
            const deliveryType = text(
              value(item, "deliveryType", "delivery_type"),
              "PICKUP",
            );
            const next = orderNext(orderStatus, deliveryType);
            const archivedUntil = text(
              value(item, "adminHiddenUntil", "admin_hidden_until"),
            );
            return (
              <article
                className={`management-card management-card--${orderStatus.toLowerCase()}`}
                key={id}
              >
                <div className="management-card__top">
                  <div>
                    <span className="order-branch-label">
                      {text(
                        value(
                          item,
                          "branchName",
                          "branch_name",
                          "branch_name_snapshot",
                        ),
                        "Cabang",
                      )}
                    </span>
                    <strong>
                      {text(value(item, "orderNumber", "order_number"))}
                    </strong>
                  </div>
                  <StatusBadge
                    status={paymentsOnly ? paymentStatus : orderStatus}
                  />
                </div>
                {archivedUntil && (
                  <Notice tone="warning">
                    Diarsipkan Admin{" "}
                    {text(
                      value(
                        item,
                        "adminHiddenBranches",
                        "admin_hidden_branches",
                      ),
                    )}
                    . Tersedia untuk Owner sampai{" "}
                    {formatDateTime(archivedUntil)}.
                  </Notice>
                )}
                <div className="management-card__facts management-card__facts--six">
                  <span>
                    <small>Pelanggan</small>
                    <strong>
                      {text(
                        value(
                          item,
                          "customerName",
                          "customer_name",
                          "customer_name_snapshot",
                        ),
                      )}
                    </strong>
                  </span>
                  <span>
                    <small>WhatsApp</small>
                    <strong>
                      {text(
                        value(
                          item,
                          "whatsappNumber",
                          "whatsapp_number",
                          "customer_whatsapp_snapshot",
                        ),
                      )}
                    </strong>
                  </span>
                  <span>
                    <small>Total</small>
                    <strong>{formatRupiah(number(item.total))}</strong>
                  </span>
                  <span>
                    <small>Pengiriman</small>
                    <strong>
                      {deliveryType === "DELIVERY" ? "Antar" : "Ambil"}
                    </strong>
                  </span>
                  <span>
                    <small>Notifikasi WA</small>
                    <strong>
                      {text(
                        value(item, "whatsappStatuses", "whatsapp_statuses"),
                        "Belum dikirim",
                      )}
                    </strong>
                  </span>
                  <span>
                    <small>Waktu</small>
                    <strong>
                      {formatDateTime(
                        text(value(item, "createdAt", "created_at")),
                      )}
                    </strong>
                  </span>
                </div>
                <div className="management-card__actions">
                  {paymentsOnly ? (
                    <>
                      <Button
                        busy={busyId === id}
                        onClick={() => void update(item, "PAID", true)}
                      >
                        Tandai Dibayar
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => void update(item, "REJECTED", true)}
                      >
                        Tolak
                      </Button>
                    </>
                  ) : (
                    <>
                      {next && (
                        <Button
                          busy={busyId === id}
                          onClick={() => void update(item, next)}
                        >
                          Lanjut: {statusLabel(next)}
                        </Button>
                      )}
                      {orderStatus === "DELIVERING" && (
                        <Button
                          variant="secondary"
                          onClick={() => void update(item, "COMPLETED")}
                        >
                          Selesai Manual
                        </Button>
                      )}
                      {!["COMPLETED", "CANCELLED", "REJECTED"].includes(
                        orderStatus,
                      ) && (
                        <Button
                          variant="danger"
                          onClick={() => void update(item, "CANCELLED")}
                        >
                          Batalkan
                        </Button>
                      )}
                      {role === "ADMIN" &&
                        ["COMPLETED", "CANCELLED", "REJECTED"].includes(
                          orderStatus,
                        ) && (
                          <Button
                            variant="danger"
                            onClick={() => setArchiveTarget(item)}
                            icon={<Trash2 />}
                          >
                            Hapus dari Admin
                          </Button>
                        )}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
      <Modal
        open={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(undefined)}
        title="Hapus pesanan dari Admin?"
        description="Pesanan hilang dari daftar Admin. Owner masih dapat melihat arsip ini selama 30 hari; data audit dan laporan tidak dirusak."
      >
        <div className="modal-actions">
          <Button
            variant="secondary"
            onClick={() => setArchiveTarget(undefined)}
          >
            Batal
          </Button>
          <Button
            variant="danger"
            busy={Boolean(busyId)}
            onClick={() => void archive()}
          >
            Hapus & Arsipkan
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export function CustomerArchivePage({ role }: { role: SessionUser["role"] }) {
  const endpoint =
    role === "OWNER" ? "/api/owner/customers" : "/api/admin/customers";
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [target, setTarget] = useState<ApiRecord>();
  const [retention, setRetention] = useState<ApiRecord[]>([]);
  const [retentionBusyId, setRetentionBusyId] = useState<string>();
  const [retentionNotice, setRetentionNotice] = useState<string>();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const customerPayload = await apiRequest<unknown>(endpoint);
      setItems(normalizeCollection(customerPayload).items);
      if (role === "OWNER") {
        const retentionPayload = await apiRequest<unknown>(
          "/api/owner/order-retention",
        );
        setRetention(normalizeCollection(retentionPayload).items);
      }
      setError(undefined);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Pelanggan belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, [endpoint, role]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const archive = async () => {
    if (!target) return;
    try {
      await apiRequest(
        `/api/admin/customers/${encodeURIComponent(text(target.id))}`,
        {
          method: "DELETE",
          ...jsonBody({ reason: "Diarsipkan manual oleh Admin" }),
        },
      );
      setTarget(undefined);
      setNotice(
        "Pelanggan dihapus dari Admin dan tetap terlihat Owner selama 30 hari.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Pelanggan belum dapat dihapus.",
      );
    }
  };

  const saveRetention = async (setting: ApiRecord) => {
    const id = text(value(setting, "branchId", "branch_id"));
    if (!id) return;
    setRetentionBusyId(id);
    setRetentionNotice(undefined);
    try {
      await apiRequest(
        `/api/owner/order-retention/${encodeURIComponent(id)}`,
        {
          method: "PUT",
          ...jsonBody({
            isEnabled: Boolean(value(setting, "isEnabled", "is_enabled")),
            retentionMonths: number(
              value(setting, "retentionMonths", "retention_months"),
              12,
            ),
          }),
        },
      );
      setRetentionNotice(
        `Pengaturan riwayat ${text(value(setting, "branchName", "branch_name"), "cabang")} berhasil disimpan.`,
      );
      await load();
    } catch (caught) {
      setRetentionNotice(
        caught instanceof Error
          ? caught.message
          : "Pengaturan pembersihan belum berhasil disimpan.",
      );
    } finally {
      setRetentionBusyId(undefined);
    }
  };

  const runRetention = async () => {
    setRetentionBusyId("all");
    setRetentionNotice(undefined);
    try {
      const result = (await apiRequest<unknown>(
        "/api/owner/order-retention/run",
        { method: "POST", ...jsonBody({ branchId: null }) },
      )) as ApiRecord;
      const count = number(value(result, "purgedOrders", "purged_orders"));
      setRetentionNotice(
        count > 0
          ? `${count} riwayat order lama berhasil dibersihkan. Total belanja dan poin tetap tersimpan.`
          : "Pemeriksaan selesai. Belum ada riwayat order yang melewati batas waktu.",
      );
      await load();
    } catch (caught) {
      setRetentionNotice(
        caught instanceof Error
          ? caught.message
          : "Pembersihan riwayat belum berhasil dijalankan.",
      );
    } finally {
      setRetentionBusyId(undefined);
    }
  };
  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Hubungan pelanggan</span>
          <h1>Pelanggan</h1>
          <p>
            {role === "ADMIN"
              ? "Pelanggan cabang Anda. Penghapusan menjadi arsip Owner selama 30 hari."
              : "Pelanggan semua cabang, termasuk salinan arsip Admin yang masih dalam masa 30 hari."}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void load()}
          icon={<RefreshCw />}
        >
          Perbarui
        </Button>
      </div>
      {notice && (
        <Notice tone={notice.includes("30 hari") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      {role === "OWNER" && (
        <section className="dashboard-panel order-retention-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Data per cabang</span>
              <h2>Pembersihan Riwayat Order</h2>
              <p>
                Opsional. Jika aktif, detail riwayat order terminal yang lebih
                lama dari batas akan dibersihkan otomatis untuk menghemat data.
              </p>
            </div>
            <Button
              variant="secondary"
              busy={retentionBusyId === "all"}
              disabled={Boolean(retentionBusyId && retentionBusyId !== "all")}
              onClick={() => void runRetention()}
            >
              Periksa & Bersihkan Sekarang
            </Button>
          </div>
          <Notice tone="warning">
            Yang dibersihkan hanya rincian item, alamat/catatan, timeline, dan
            notifikasi order lama. <strong>Data pelanggan, total belanja,
            ringkasan laporan, saldo poin, dan riwayat alasan poin tidak
            dihapus.</strong>
          </Notice>
          {retentionNotice && (
            <Notice
              tone={
                retentionNotice.includes("berhasil") ||
                retentionNotice.includes("selesai")
                  ? "success"
                  : "danger"
              }
            >
              {retentionNotice}
            </Notice>
          )}
          <div className="order-retention-grid">
            {retention.map((setting) => {
              const id = text(value(setting, "branchId", "branch_id"));
              const enabled = Boolean(
                value(setting, "isEnabled", "is_enabled"),
              );
              const months = number(
                value(setting, "retentionMonths", "retention_months"),
                12,
              );
              return (
                <article key={id}>
                  <div>
                    <strong>
                      {text(value(setting, "branchName", "branch_name"))}
                    </strong>
                    <small>
                      Kode cabang {text(value(setting, "branchCode", "branch_code"))}
                    </small>
                  </div>
                  <label className="toggle-setting">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(event) =>
                        setRetention((current) =>
                          current.map((row) =>
                            text(value(row, "branchId", "branch_id")) === id
                              ? {
                                  ...row,
                                  isEnabled: event.target.checked,
                                  is_enabled: event.target.checked ? 1 : 0,
                                }
                              : row,
                          ),
                        )
                      }
                    />
                    <span>
                      <strong>Aktifkan pembersihan otomatis</strong>
                      <small>Awalnya nonaktif dan hanya berjalan jika dicentang.</small>
                    </span>
                  </label>
                  <label>
                    Simpan riwayat selama
                    <select
                      value={months}
                      onChange={(event) =>
                        setRetention((current) =>
                          current.map((row) =>
                            text(value(row, "branchId", "branch_id")) === id
                              ? {
                                  ...row,
                                  retentionMonths: Number(event.target.value),
                                  retention_months: Number(event.target.value),
                                }
                              : row,
                          ),
                        )
                      }
                    >
                      <option value="6">6 bulan</option>
                      <option value="12">1 tahun</option>
                    </select>
                  </label>
                  <small>
                    {number(value(setting, "eligibleOrders", "eligible_orders"))} riwayat saat ini melewati batas.
                  </small>
                  <Button
                    onClick={() => void saveRetention(setting)}
                    busy={retentionBusyId === id}
                    disabled={Boolean(retentionBusyId && retentionBusyId !== id)}
                    icon={<Save aria-hidden="true" />}
                  >
                    Simpan Cabang
                  </Button>
                </article>
              );
            })}
          </div>
        </section>
      )}
      {loading && <Skeleton rows={5} cards />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && items.length === 0 && (
        <EmptyState title="Belum ada pelanggan" />
      )}
      {!loading && !error && (
        <div className="access-card-grid">
          {items.map((item) => {
            const archivedUntil = text(
              value(item, "adminHiddenUntil", "admin_hidden_until"),
            );
            return (
              <article className="access-card" key={text(item.id)}>
                <div className="access-card__header">
                  <strong>{text(item.name)}</strong>
                  {archivedUntil ? (
                    <StatusBadge status="archived" />
                  ) : (
                    <StatusBadge status="active" />
                  )}
                </div>
                <p>{text(value(item, "whatsappNumber", "whatsapp_number"))}</p>
                <dl>
                  <div>
                    <dt>Total pesanan</dt>
                    <dd>{number(value(item, "orderCount", "order_count"))}</dd>
                  </div>
                  <div>
                    <dt>Belanja tercatat</dt>
                    <dd>
                      {formatRupiah(
                        number(value(item, "totalSpend", "total_spend")),
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Terakhir memesan</dt>
                    <dd>
                      {formatDateTime(
                        text(value(item, "lastOrderAt", "last_order_at")),
                      )}
                    </dd>
                  </div>
                </dl>
                {archivedUntil && (
                  <Notice tone="warning">
                    Diarsipkan oleh{" "}
                    {text(
                      value(
                        item,
                        "adminHiddenBranches",
                        "admin_hidden_branches",
                      ),
                    )}
                    . Tersedia sampai {formatDateTime(archivedUntil)}.
                  </Notice>
                )}
                {role === "ADMIN" && (
                  <Button
                    variant="danger"
                    onClick={() => setTarget(item)}
                    icon={<Trash2 />}
                  >
                    Hapus dari Admin
                  </Button>
                )}
              </article>
            );
          })}
        </div>
      )}
      <Modal
        open={Boolean(target)}
        onClose={() => setTarget(undefined)}
        title="Hapus pelanggan dari Admin?"
        description="Pelanggan hilang dari daftar cabang. Owner masih melihat salinannya selama 30 hari."
      >
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setTarget(undefined)}>
            Batal
          </Button>
          <Button variant="danger" onClick={() => void archive()}>
            Hapus & Arsipkan
          </Button>
        </div>
      </Modal>
    </div>
  );
}

interface HomepageForm {
  id?: string;
  title: string;
  subtitle: string;
  sectionType: string;
  categoryId: string;
  branchId: string;
  mediaId: string;
  ctaText: string;
  ctaUrl: string;
  displayOrder: string;
  limit: string;
  isActive: boolean;
  productIds: string[];
}

interface HomepageProductOption {
  id: string;
  name: string;
  categoryName: string;
  mediaId?: string;
}

function toHomepageProductOptions(payload: unknown): HomepageProductOption[] {
  return normalizeCollection(payload).items.map((item) => {
    const firstBranch = Array.isArray(item.branches)
      ? (item.branches[0] as ApiRecord | undefined)
      : undefined;
    return {
      id: text(item.id),
      name: text(item.name),
      categoryName: text(
        value(item, "categoryName", "category_name"),
        "Tanpa kategori",
      ),
      mediaId:
        text(
          value(
            item,
            "globalMediaId",
            "global_media_id",
            ...(firstBranch ? ["mediaId", "media_id"] : []),
          ),
        ) ||
        (firstBranch
          ? text(value(firstBranch, "mediaId", "media_id"))
          : undefined),
    };
  });
}

const blankHomepage = (displayOrder = 1): HomepageForm => ({
  title: "",
  subtitle: "",
  sectionType: "FEATURED_PRODUCTS",
  categoryId: "",
  branchId: "",
  mediaId: "",
  ctaText: "",
  ctaUrl: "",
  displayOrder: String(displayOrder),
  limit: "8",
  isActive: true,
  productIds: [],
});

const homepageTypeLabel: Record<string, string> = {
  CATEGORY_PRODUCTS: "Otomatis dari kategori",
  FEATURED_PRODUCTS: "Otomatis produk unggulan",
  PROMO_PRODUCTS: "Otomatis produk promo",
  CUSTOM_PRODUCTS: "Manual pilih produk",
  BANNER: "Banner manual",
};

export function HomepageManagementPage() {
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [branches, setBranches] = useState<ApiRecord[]>([]);
  const [products, setProducts] = useState<HomepageProductOption[]>([]);
  const [productPagination, setProductPagination] =
    useState<OwnerProductPagination>(INITIAL_OWNER_PRODUCT_PAGINATION);
  const [media, setMedia] = useState<MediaOption[]>([]);
  const [form, setForm] = useState<HomepageForm>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [productPageBusy, setProductPageBusy] = useState(false);
  const [notice, setNotice] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        sectionPayload,
        categoryPayload,
        branchPayload,
        productPayload,
        mediaPayload,
      ] = await Promise.all([
        apiRequest<unknown>("/api/owner/homepage"),
        apiRequest<unknown>("/api/owner/categories"),
        apiRequest<unknown>("/api/owner/branches"),
        apiRequest<unknown>(
          `/api/owner/products${queryString({ page: 1, limit: OWNER_PRODUCT_PAGE_SIZE })}`,
        ),
        apiRequest<unknown>("/api/owner/media"),
      ]);
      setItems(normalizeCollection(sectionPayload).items);
      setCategories(
        normalizeCollection(categoryPayload).items.map((item) => ({
          id: text(item.id),
          name: text(item.name),
          slug: text(item.slug),
          description: text(item.description),
        })),
      );
      setBranches(normalizeCollection(branchPayload).items);
      setProducts(toHomepageProductOptions(productPayload));
      setProductPagination(toOwnerProductPagination(productPayload));
      setMedia(toMedia(mediaPayload));
      setNotice(undefined);
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Tampilan website belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const loadMoreProducts = async () => {
    if (productPageBusy || !productPagination.hasMore) return;
    setProductPageBusy(true);
    try {
      const payload = await apiRequest<unknown>(
        `/api/owner/products${queryString({
          page: productPagination.page + 1,
          limit: OWNER_PRODUCT_PAGE_SIZE,
        })}`,
      );
      const nextProducts = toHomepageProductOptions(payload);
      setProducts((current) => {
        const known = new Set(current.map((product) => product.id));
        return [
          ...current,
          ...nextProducts.filter((product) => !known.has(product.id)),
        ];
      });
      setProductPagination(toOwnerProductPagination(payload));
      setNotice(undefined);
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Produk berikutnya belum dapat dimuat.",
      );
    } finally {
      setProductPageBusy(false);
    }
  };

  const openEdit = (item: ApiRecord) => {
    let configuration: ApiRecord = {};
    try {
      const raw = value(
        item,
        "configuration",
        "configurationJson",
        "configuration_json",
      );
      const parsed: unknown = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        configuration = parsed as ApiRecord;
      }
    } catch {
      configuration = {};
    }
    const selected = value(item, "productIds", "product_ids");
    setForm({
      id: text(item.id),
      title: text(item.title),
      subtitle: text(item.subtitle),
      sectionType: text(
        value(item, "sectionType", "section_type"),
        "FEATURED_PRODUCTS",
      ),
      categoryId: text(value(item, "categoryId", "category_id")),
      branchId: text(value(item, "branchId", "branch_id")),
      mediaId: text(value(item, "mediaId", "media_id")),
      ctaText: text(value(item, "ctaText", "cta_text")),
      ctaUrl: text(value(item, "ctaUrl", "cta_url")),
      displayOrder: text(value(item, "displayOrder", "display_order"), "1"),
      limit: text(configuration.limit, "8"),
      isActive: bool(value(item, "isActive", "is_active"), true),
      productIds: Array.isArray(selected)
        ? selected.map((id) => String(id))
        : typeof selected === "string"
          ? selected.split(",").filter(Boolean)
          : [],
    });
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    if (form.sectionType === "CATEGORY_PRODUCTS" && !form.categoryId) {
      setNotice("Pilih kategori untuk section kategori.");
      return;
    }
    if (
      form.sectionType === "CUSTOM_PRODUCTS" &&
      form.productIds.length === 0
    ) {
      setNotice("Pilih minimal satu produk untuk section manual.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        subtitle: form.subtitle || null,
        sectionType: form.sectionType,
        categoryId:
          form.sectionType === "CATEGORY_PRODUCTS" ? form.categoryId : null,
        branchId: form.branchId || null,
        mediaId: form.sectionType === "BANNER" ? form.mediaId || null : null,
        ctaText: form.ctaText || null,
        ctaUrl: form.ctaUrl || null,
        displayOrder: Number(form.displayOrder) || 1,
        isActive: form.isActive,
        configuration: {
          limit: Math.min(12, Math.max(1, Number(form.limit) || 8)),
        },
        productIds:
          form.sectionType === "CUSTOM_PRODUCTS" ? form.productIds : [],
      };
      await apiRequest(
        form.id
          ? `/api/owner/homepage/${encodeURIComponent(form.id)}`
          : "/api/owner/homepage",
        {
          method: form.id ? "PATCH" : "POST",
          ...jsonBody(payload),
        },
      );
      setForm(undefined);
      setNotice(
        "Section homepage berhasil disimpan dan langsung aktif sesuai pilihan.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Section homepage belum berhasil disimpan.",
      );
    } finally {
      setBusy(false);
    }
  };

  const toggleSection = async (item: ApiRecord) => {
    setBusy(true);
    try {
      await apiRequest(
        `/api/owner/homepage/${encodeURIComponent(text(item.id))}`,
        {
          method: "PATCH",
          ...jsonBody({
            isActive: !bool(value(item, "isActive", "is_active"), true),
          }),
        },
      );
      setNotice("Tampilan section berhasil diperbarui.");
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Section belum dapat diubah.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Kelola main website</span>
          <h1>Tampilan Website</h1>
          <p>
            Pilih section otomatis, tampilkan kategori tertentu, atau susun
            produk manual untuk semua cabang maupun satu cabang.
          </p>
        </div>
        <div className="heading-actions">
          <Button
            variant="secondary"
            onClick={() => void load()}
            icon={<RefreshCw />}
          >
            Perbarui
          </Button>
          <Button
            onClick={() => setForm(blankHomepage(items.length + 1))}
            icon={<Plus />}
          >
            Tambah Section
          </Button>
        </div>
      </div>
      <Notice tone="info">
        Section manual memberi pilihan produk satu per satu. Section otomatis
        akan selalu mengikuti promo, unggulan, atau kategori yang dipilih.
      </Notice>
      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      {/* Live streaming disembunyikan sementara. API dan data dipertahankan agar dapat dilanjutkan nanti.
      <section className="dashboard-panel homepage-live-admin">
        <div className="homepage-live-admin__heading">
          <span className="homepage-live-admin__icon">
            <Radio aria-hidden="true" />
          </span>
          <div>
            <span className="eyebrow">Live di main website</span>
            <h2>Live Streaming YouTube / TikTok</h2>
            <p>
              Jika diaktifkan, bingkai TV tampil di atas bagian “Segar untuk
              keluarga” pada ponsel dan komputer.
            </p>
          </div>
        </div>
        <form className="homepage-live-form" onSubmit={(event) => void saveLiveStream(event)}>
          <label className="check-field homepage-live-toggle">
            <input
              type="checkbox"
              checked={liveStream.enabled}
              onChange={(event) =>
                setLiveStream({ ...liveStream, enabled: event.target.checked })
              }
            />
            <span>
              <strong>Tampilkan live streaming</strong>
              <small>Hilangkan centang untuk menyembunyikannya tanpa menghapus link.</small>
            </span>
          </label>
          <div className="two-field-grid">
            <label>
              Platform live
              <select
                value={liveStream.platform}
                onChange={(event) =>
                  setLiveStream({
                    ...liveStream,
                    platform:
                      event.target.value === "TIKTOK" ? "TIKTOK" : "YOUTUBE",
                  })
                }
              >
                <option value="YOUTUBE">YouTube</option>
                <option value="TIKTOK">TikTok LIVE</option>
              </select>
            </label>
            <label>
              Judul di atas TV
              <input
                required
                maxLength={100}
                value={liveStream.title}
                onChange={(event) =>
                  setLiveStream({ ...liveStream, title: event.target.value })
                }
                placeholder="Sedap Sayur Live"
              />
            </label>
          </div>
          <label>
            Link live streaming
            <input
              type="url"
              inputMode="url"
              maxLength={500}
              required={liveStream.enabled}
              value={liveStream.url}
              onChange={(event) =>
                setLiveStream({ ...liveStream, url: event.target.value })
              }
              placeholder={
                liveStream.platform === "YOUTUBE"
                  ? "https://www.youtube.com/watch?v=..."
                  : "https://www.tiktok.com/@namatoko/live"
              }
            />
          </label>
          {liveStream.url && (
            <div className={`live-link-status ${livePreview ? "live-link-status--valid" : "live-link-status--invalid"}`}>
              <span>
                {livePreview
                  ? `Link ${liveStream.platform === "YOUTUBE" ? "YouTube" : "TikTok"} dikenali.`
                  : "Link belum sesuai dengan platform yang dipilih."}
              </span>
              {livePreview && (
                <a href={livePreview.originalUrl} target="_blank" rel="noreferrer">
                  Periksa link <ExternalLink aria-hidden="true" />
                </a>
              )}
            </div>
          )}
          {liveStream.platform === "TIKTOK" && (
            <Notice tone="info">
              TikTok LIVE hanya dapat diputar di website setelah domain website
              disetujui TikTok. Tombol “Buka di TikTok” tetap tersedia sebagai
              cadangan.
            </Notice>
          )}
          <div className="homepage-live-form__actions">
            <Button type="submit" busy={liveBusy} icon={<Save />}>
              Simpan Live Streaming
            </Button>
          </div>
        </form>
      </section>
      */}
      {loading ? (
        <Skeleton rows={5} cards />
      ) : items.length === 0 ? (
        <EmptyState title="Belum ada section homepage" />
      ) : (
        <div className="homepage-section-grid">
          {items.map((item) => {
            const type = text(value(item, "sectionType", "section_type"));
            const active = bool(value(item, "isActive", "is_active"), true);
            const branchName = branches.find(
              (branch) =>
                text(branch.id) === text(value(item, "branchId", "branch_id")),
            );
            return (
              <article className="homepage-section-card" key={text(item.id)}>
                <div className="access-card__header">
                  <span className="branch-code">
                    {text(value(item, "displayOrder", "display_order"), "1")}
                  </span>
                  <StatusBadge status={active ? "active" : "inactive"} />
                </div>
                <span>{homepageTypeLabel[type] ?? type}</span>
                <h2>{text(item.title)}</h2>
                <p>{text(item.subtitle, "Tanpa subjudul")}</p>
                <small>
                  {branchName ? text(branchName.name) : "Semua cabang"}
                </small>
                <div className="management-card__actions">
                  <Button
                    variant="secondary"
                    onClick={() => openEdit(item)}
                    icon={<Pencil />}
                  >
                    Ubah & Pilih
                  </Button>
                  <Button
                    variant={active ? "danger" : "secondary"}
                    disabled={busy}
                    onClick={() => void toggleSection(item)}
                  >
                    {active ? "Sembunyikan" : "Tampilkan"}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(form)}
        onClose={() => setForm(undefined)}
        title={form?.id ? "Ubah Section Homepage" : "Tambah Section Homepage"}
        description="Semua pilihan dapat diubah lagi kapan saja."
      >
        {form && (
          <form className="modal-form" onSubmit={(event) => void save(event)}>
            <label>
              Cara mengisi section
              <select
                value={form.sectionType}
                onChange={(event) =>
                  setForm({
                    ...form,
                    sectionType: event.target.value,
                    categoryId: "",
                    productIds: [],
                  })
                }
              >
                {Object.entries(homepageTypeLabel).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <div className="two-field-grid">
              <label>
                Judul section
                <input
                  required
                  maxLength={140}
                  value={form.title}
                  onChange={(event) =>
                    setForm({ ...form, title: event.target.value })
                  }
                  placeholder="Contoh: Seafood Segar"
                />
              </label>
              <label>
                Berlaku untuk cabang
                <select
                  value={form.branchId}
                  onChange={(event) =>
                    setForm({ ...form, branchId: event.target.value })
                  }
                >
                  <option value="">Semua cabang</option>
                  {branches.map((branch) => (
                    <option value={text(branch.id)} key={text(branch.id)}>
                      {text(branch.name)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Subjudul (opsional)
              <input
                maxLength={300}
                value={form.subtitle}
                onChange={(event) =>
                  setForm({ ...form, subtitle: event.target.value })
                }
              />
            </label>
            {form.sectionType === "CATEGORY_PRODUCTS" && (
              <label>
                Pilih kategori
                <select
                  required
                  value={form.categoryId}
                  onChange={(event) =>
                    setForm({ ...form, categoryId: event.target.value })
                  }
                >
                  <option value="">Pilih kategori</option>
                  {categories.map((category) => (
                    <option value={category.id} key={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {form.sectionType === "CUSTOM_PRODUCTS" && (
              <section className="homepage-product-picker">
                <h3>Pilih produk manual</h3>
                <p>{form.productIds.length} produk dipilih · maksimal 24</p>
                <div>
                  {products.map((product) => (
                    <label key={product.id}>
                      <input
                        type="checkbox"
                        checked={form.productIds.includes(product.id)}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            productIds: event.target.checked
                              ? [...form.productIds, product.id].slice(0, 24)
                              : form.productIds.filter(
                                  (id) => id !== product.id,
                                ),
                          })
                        }
                      />
                      <MediaImage
                        mediaId={product.mediaId}
                        alt={product.name}
                      />
                      <span>
                        <strong>{product.name}</strong>
                        <small>{product.categoryName}</small>
                      </span>
                    </label>
                  ))}
                </div>
                {productPagination.hasMore && (
                  <button
                    className="button button--secondary"
                    type="button"
                    disabled={productPageBusy}
                    onClick={() => void loadMoreProducts()}
                  >
                    {productPageBusy
                      ? "Memuat produk…"
                      : "Muat produk berikutnya"}
                  </button>
                )}
              </section>
            )}
            {form.sectionType === "BANNER" && (
              <section>
                <h3>Pilih gambar banner</h3>
                <div className="media-choice-grid media-choice-grid--compact">
                  {media.map((item) => (
                    <button
                      type="button"
                      className={
                        form.mediaId === item.id
                          ? "media-choice media-choice--selected"
                          : "media-choice"
                      }
                      onClick={() => setForm({ ...form, mediaId: item.id })}
                      key={item.id}
                    >
                      <img
                        src={`/api/media/private/${encodeURIComponent(item.id)}`}
                        alt={item.filename}
                      />
                      <strong>{item.filename}</strong>
                    </button>
                  ))}
                </div>
              </section>
            )}
            <div className="two-field-grid">
              <label>
                Posisi tampil
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={form.displayOrder}
                  onChange={(event) =>
                    setForm({ ...form, displayOrder: event.target.value })
                  }
                />
                <span>1 paling atas; angka lebih besar tampil setelahnya.</span>
              </label>
              <label>
                Maksimal produk
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={form.limit}
                  onChange={(event) =>
                    setForm({ ...form, limit: event.target.value })
                  }
                />
              </label>
            </div>
            <div className="two-field-grid">
              <label>
                Tulisan tombol (opsional)
                <input
                  value={form.ctaText}
                  onChange={(event) =>
                    setForm({ ...form, ctaText: event.target.value })
                  }
                />
              </label>
              <label>
                Link tombol (opsional)
                <input
                  value={form.ctaUrl}
                  onChange={(event) =>
                    setForm({ ...form, ctaUrl: event.target.value })
                  }
                  placeholder="/kategori/seafood"
                />
              </label>
            </div>
            <label className="check-field">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) =>
                  setForm({ ...form, isActive: event.target.checked })
                }
              />{" "}
              Tampilkan section di website
            </label>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setForm(undefined)}
              >
                Batal
              </Button>
              <Button type="submit" busy={busy} icon={<Save />}>
                Simpan Tampilan
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

interface PaymentForm {
  id?: string;
  branchId: string;
  name: string;
  type: string;
  instructions: string;
  accountName: string;
  accountNumber: string;
  mediaId: string;
  isActive: boolean;
  displayOrder: string;
}
const blankPayment = (): PaymentForm => ({
  branchId: "",
  name: "",
  type: "BANK_TRANSFER",
  instructions: "",
  accountName: "",
  accountNumber: "",
  mediaId: "",
  isActive: true,
  displayOrder: "1",
});

export function PaymentMethodsPage() {
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [branches, setBranches] = useState<ApiRecord[]>([]);
  const [media, setMedia] = useState<MediaOption[]>([]);
  const [form, setForm] = useState<PaymentForm>();
  const [notice, setNotice] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [optimized, setOptimized] = useState<OptimizedImage>();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b, m] = await Promise.all([
        apiRequest<unknown>("/api/owner/payment-methods"),
        apiRequest<unknown>("/api/owner/branches"),
        apiRequest<unknown>("/api/owner/media"),
      ]);
      setItems(normalizeCollection(p).items);
      setBranches(normalizeCollection(b).items);
      setMedia(toMedia(m));
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Metode pembayaran belum dapat dimuat.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const openEdit = (item: ApiRecord) =>
    setForm({
      id: text(item.id),
      branchId: text(value(item, "branchId", "branch_id")),
      name: text(item.name),
      type: text(item.type, "BANK_TRANSFER"),
      instructions: text(item.instructions),
      accountName: text(value(item, "accountName", "account_name")),
      accountNumber: text(value(item, "accountNumber", "account_number")),
      mediaId: text(value(item, "mediaId", "media_id")),
      isActive: bool(value(item, "isActive", "is_active"), true),
      displayOrder: text(value(item, "displayOrder", "display_order"), "0"),
    });
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    try {
      let mediaId = form.mediaId || null;
      if (optimized) {
        const uploaded = await apiRequest<{ id: string }>("/api/media", {
          method: "POST",
          ...jsonBody({
            filename: optimized.filename,
            originalFilename: optimized.filename,
            mediaType: "other",
            altText: `Logo ${form.name}`,
            dataBase64: await blobToBase64(optimized.blob),
          }),
        });
        mediaId = uploaded.id;
      }
      const payload = {
        branchId: form.branchId || null,
        name: form.name,
        type: form.type,
        instructions: form.instructions || null,
        accountName: form.accountName || null,
        accountNumber: form.accountNumber || null,
        mediaId,
        isActive: form.isActive,
        displayOrder: Number(form.displayOrder) || 0,
      };
      await apiRequest(
        form.id
          ? `/api/owner/payment-methods/${encodeURIComponent(form.id)}`
          : "/api/owner/payment-methods",
        { method: form.id ? "PATCH" : "POST", ...jsonBody(payload) },
      );
      setForm(undefined);
      setOptimized(undefined);
      setNotice(
        "Metode pembayaran berhasil disimpan dan langsung tersedia di checkout sesuai cabang.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Metode pembayaran belum berhasil disimpan.",
      );
    } finally {
      setBusy(false);
    }
  };
  const chooseLogo = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setOptimized(await optimizeProductImage(file));
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Logo belum dapat diproses.",
      );
    }
  };
  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Checkout pelanggan</span>
          <h1>Metode Pembayaran</h1>
          <p>
            Atur bank, nama rekening, nomor rekening, instruksi, cabang, dan
            logo kecil.
          </p>
        </div>
        <div className="heading-actions">
          <Button
            variant="secondary"
            onClick={() => void load()}
            icon={<RefreshCw />}
          >
            Perbarui
          </Button>
          <Button onClick={() => setForm(blankPayment())} icon={<Plus />}>
            Tambah Pembayaran
          </Button>
        </div>
      </div>
      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      {loading ? (
        <Skeleton rows={5} cards />
      ) : (
        <div className="payment-admin-grid">
          {items.map((item) => (
            <article className="payment-admin-card" key={text(item.id)}>
              <div className="payment-logo">
                <MediaImage
                  mediaId={
                    text(value(item, "mediaId", "media_id")) || undefined
                  }
                  alt={`Logo ${text(item.name)}`}
                />
              </div>
              <div>
                <span>
                  {text(
                    value(item, "branchName", "branch_name"),
                    "Semua cabang",
                  )}
                </span>
                <h2>{text(item.name)}</h2>
                <p>
                  {text(value(item, "accountName", "account_name")) ||
                    "Tanpa nama rekening"}
                  <br />
                  <strong>
                    {text(value(item, "accountNumber", "account_number")) ||
                      "Tanpa nomor rekening"}
                  </strong>
                </p>
              </div>
              <StatusBadge
                status={
                  bool(value(item, "isActive", "is_active"))
                    ? "active"
                    : "inactive"
                }
              />
              <Button
                variant="secondary"
                onClick={() => openEdit(item)}
                icon={<Pencil />}
              >
                Ubah
              </Button>
            </article>
          ))}
        </div>
      )}
      <Modal
        open={Boolean(form)}
        onClose={() => {
          setForm(undefined);
          setOptimized(undefined);
        }}
        title={form?.id ? "Ubah Pembayaran" : "Tambah Pembayaran"}
        description="Logo ditampilkan kecil dan rapi di checkout."
      >
        {form && (
          <form className="modal-form" onSubmit={(event) => void save(event)}>
            <label>
              Cabang
              <select
                value={form.branchId}
                onChange={(event) =>
                  setForm({ ...form, branchId: event.target.value })
                }
              >
                <option value="">Semua cabang</option>
                {branches.map((branch) => (
                  <option value={text(branch.id)} key={text(branch.id)}>
                    {text(branch.name)}
                  </option>
                ))}
              </select>
            </label>
            <div className="two-field-grid">
              <label>
                Nama metode
                <input
                  required
                  value={form.name}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                  placeholder="Transfer Bank BCA"
                />
              </label>
              <label>
                Jenis
                <select
                  value={form.type}
                  onChange={(event) =>
                    setForm({ ...form, type: event.target.value })
                  }
                >
                  <option value="BANK_TRANSFER">Transfer bank</option>
                  <option value="COD">Bayar di tempat</option>
                  <option value="PAY_AT_STORE">Bayar saat ambil</option>
                  <option value="QRIS_MANUAL">QRIS manual</option>
                </select>
              </label>
            </div>
            <div className="two-field-grid">
              <label>
                Nama pemilik rekening
                <input
                  value={form.accountName}
                  onChange={(event) =>
                    setForm({ ...form, accountName: event.target.value })
                  }
                />
              </label>
              <label>
                Nomor rekening
                <input
                  value={form.accountNumber}
                  onChange={(event) =>
                    setForm({ ...form, accountNumber: event.target.value })
                  }
                />
              </label>
            </div>
            <label>
              Instruksi
              <textarea
                rows={3}
                value={form.instructions}
                onChange={(event) =>
                  setForm({ ...form, instructions: event.target.value })
                }
              />
            </label>
            <section>
              <h3>Pilih logo yang sudah ada</h3>
              <div className="media-choice-grid media-choice-grid--compact">
                {media.map((item) => (
                  <button
                    type="button"
                    className={
                      form.mediaId === item.id
                        ? "media-choice media-choice--selected"
                        : "media-choice"
                    }
                    key={item.id}
                    onClick={() => {
                      setForm({ ...form, mediaId: item.id });
                      setOptimized(undefined);
                    }}
                  >
                    <img
                      src={`/api/media/private/${encodeURIComponent(item.id)}`}
                      alt={item.filename}
                    />
                    <strong>{item.filename}</strong>
                  </button>
                ))}
              </div>
            </section>
            <label className="compact-upload">
              <ImagePlus />
              <span>Atau unggah logo baru</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void chooseLogo(event)}
              />
            </label>
            {optimized && (
              <img
                className="payment-logo-preview"
                src={URL.createObjectURL(optimized.blob)}
                alt="Pratinjau logo"
              />
            )}
            <div className="two-field-grid">
              <label>
                Posisi tampil
                <input
                  type="number"
                  min="1"
                  value={form.displayOrder}
                  onChange={(event) =>
                    setForm({ ...form, displayOrder: event.target.value })
                  }
                />
                <span>
                  1 tampil paling atas. Angka 2 tampil setelah 1, dan
                  seterusnya.
                </span>
              </label>
              <label className="check-field">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm({ ...form, isActive: event.target.checked })
                  }
                />{" "}
                Aktif di checkout
              </label>
            </div>
            <div className="modal-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setForm(undefined)}
              >
                Batal
              </Button>
              <Button type="submit" busy={busy} icon={<Save />}>
                Simpan Pembayaran
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

export function OwnerSecurityPage() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirm) {
      setNotice("Konfirmasi password baru belum sama.");
      return;
    }
    setBusy(true);
    try {
      await apiRequest("/api/owner/change-password", {
        method: "POST",
        ...jsonBody({ currentPassword, newPassword }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
      setNotice(
        "Password Owner berhasil diganti. Semua sesi Owner lain telah diputus.",
      );
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Password belum berhasil diganti.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Keamanan akun</span>
          <h1>Password Owner</h1>
          <p>
            Ganti password Owner tanpa menyimpan password lama sebagai teks.
          </p>
        </div>
      </div>
      <Notice tone="info">
        Password lama tidak bisa ditampilkan karena sistem hanya menyimpan hash.
        Setelah diganti, password baru dapat Anda lihat sebelum menekan Simpan;
        sesi Owner lain diputus otomatis.
      </Notice>
      {notice && (
        <Notice tone={notice.includes("berhasil") ? "success" : "danger"}>
          {notice}
        </Notice>
      )}
      <section className="operations-section owner-security-card">
        <ShieldCheck />
        <form className="modal-form" onSubmit={(event) => void save(event)}>
          <label>
            Password saat ini
            <input
              required
              type={show ? "text" : "password"}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </label>
          <label>
            Password baru
            <input
              required
              minLength={12}
              maxLength={128}
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </label>
          <label>
            Ulangi password baru
            <input
              required
              minLength={12}
              maxLength={128}
              type={show ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShow((value) => !value)}
            icon={show ? <EyeOff /> : <Eye />}
          >
            {show ? "Sembunyikan" : "Tampilkan"} password
          </Button>
          <Button type="submit" busy={busy} icon={<Save />}>
            Ganti Password Owner
          </Button>
        </form>
      </section>
    </div>
  );
}
